<template>
  <div class="advanced-email-builder">
    <!-- Header Section -->
    <div class="bg-white border-b border-gray-200 px-6 py-3">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-xl font-bold text-gray-900 flex items-center">
            <FileTextIcon class="w-5 h-5 mr-2 text-blue-600" />
            Welcome Email Builder
          </h1>
        </div>
        <div class="flex items-center space-x-2">
          <!-- Undo/Redo -->
          <button 
            @click="undo"
            :disabled="historyIndex <= 0"
            :class="[
              'p-2 rounded-md text-sm',
              historyIndex <= 0 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-700 hover:bg-gray-100'
            ]"
            title="Undo"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
            </svg>
          </button>
          <button 
            @click="redo"
            :disabled="historyIndex >= history.length - 1"
            :class="[
              'p-2 rounded-md text-sm',
              historyIndex >= history.length - 1 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-700 hover:bg-gray-100'
            ]"
            title="Redo"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6"/>
            </svg>
          </button>
          
          <div class="h-6 w-px bg-gray-300"></div>
          
          <!-- Save/Export Actions -->
          <button 
            @click="saveTemplate"
            class="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
          >
            <SaveIcon class="w-4 h-4 mr-1" />
            Save
          </button>
          <button 
            @click="exportHtml"
            class="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
          >
            <DownloadIcon class="w-4 h-4 mr-1" />
            Export
          </button>

          <!-- Close Builder Button (when in modal) -->
          <button 
            v-if="showCloseButton"
            @click="$emit('close')"
            class="inline-flex items-center px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-md ml-2"
          >
            <XIcon class="w-4 h-4 mr-1" />
            Close Builder
          </button>
        </div>
      </div>
    </div>

    <!-- Main Email Builder Interface -->
    <div class="flex flex-1 min-h-0">
      <!-- Left Sidebar - Component Palette -->
      <div class="w-64 bg-white border-r border-gray-200 overflow-y-auto" style="max-height: calc(85vh - 140px);">
        <div class="p-4">
          <!-- Templates Section -->
          <div class="mb-6">
            <h3 class="text-sm font-medium text-gray-900 uppercase tracking-wide mb-3">
              Templates
            </h3>
            <div class="space-y-2">
              <!-- Welcome Email Template -->
              <div 
                @click="loadTemplate(templateLibrary.find(t => t.id === 'volvo-replica'))"
                :class="[
                  'p-3 rounded-md cursor-pointer transition-colors',
                  selectedTemplate?.id === 'volvo-replica' 
                    ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                    : 'text-gray-700 hover:bg-gray-50'
                ]"
              >
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                    <FileTextIcon class="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div class="text-sm font-medium">Welcome Email</div>
                    <div class="text-xs text-gray-500">Professional welcome template</div>
                  </div>
                </div>
              </div>
              
              <!-- Empty Canvas -->
              <div 
                @click="loadTemplate(templateLibrary.find(t => t.id === 'empty'))"
                :class="[
                  'p-3 rounded-md cursor-pointer transition-colors',
                  selectedTemplate?.id === 'empty' 
                    ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                    : 'text-gray-700 hover:bg-gray-50'
                ]"
              >
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                    <FileTextIcon class="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <div class="text-sm font-medium">Empty Canvas</div>
                    <div class="text-xs text-gray-500">Start from scratch</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Components Section -->
          <div>
            <h3 class="text-sm font-medium text-gray-900 uppercase tracking-wide mb-3">
              Components
            </h3>
            <div class="grid grid-cols-2 gap-2">
              <!-- Heading -->
              <div 
                draggable="true"
                @dragstart="dragStart('heading', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Heading</span>
              </div>

              <!-- Text -->
              <div 
                draggable="true"
                @dragstart="dragStart('text', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Text</span>
              </div>

              <!-- Button -->
              <div 
                draggable="true"
                @dragstart="dragStart('button', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Button</span>
              </div>

              <!-- Image -->
              <div 
                draggable="true"
                @dragstart="dragStart('image', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Image</span>
              </div>

              <!-- Divider -->
              <div 
                draggable="true"
                @dragstart="dragStart('divider', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Divider</span>
              </div>

              <!-- Spacer -->
              <div 
                draggable="true"
                @dragstart="dragStart('spacer', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Spacer</span>
              </div>

              <!-- Columns -->
              <div 
                draggable="true"
                @dragstart="dragStart('columns', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Columns</span>
              </div>

              <!-- Hero Image -->
              <div 
                draggable="true"
                @dragstart="dragStart('hero', $event)"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-move transition-colors"
              >
                <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Hero</span>
              </div>

              <!-- Social -->
              <div 
                @click="showSocialsModal = true"
                class="flex flex-col items-center p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mb-1">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
                <span class="text-xs text-gray-600">Social</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Center - Email Canvas -->
      <div class="flex-1 bg-gray-100 flex flex-col min-h-0">
        <!-- Canvas Area -->
        <div class="flex-1 overflow-y-auto p-6">
          <div class="max-w-2xl mx-auto">
            <!-- Email Container -->
            <div 
              class="shadow-lg overflow-hidden min-h-[600px]"
              :style="{ 
                borderRadius: emailSettings.borderRadius + 'px',
                backgroundColor: emailSettings.backgroundColor
              }"
              @drop="onDrop($event)"
              @dragover="onDragOver($event)"
              @dragenter="onDragEnter($event)"
              @dragleave="onDragLeave($event)"
            >
              <!-- Email Header -->
              <div 
                v-if="emailSettings.showHeader"
                class="text-center"
                :style="{ 
                  backgroundColor: emailSettings.headerColor || '#1f2937',
                  padding: emailSettings.containerPadding + 'px'
                }"
              >
                <div class="w-12 h-12 bg-white rounded mx-auto mb-3 flex items-center justify-center">
                  <span class="text-gray-800 font-bold text-lg">{{ emailSettings.logoText || 'FG' }}</span>
                </div>
                <h1 class="text-white text-lg font-medium">{{ emailSettings.headerTitle || 'Welcome! 👋' }}</h1>
              </div>

              <!-- Email Body -->
              <div 
                id="email-body"
                :style="{ padding: emailSettings.containerPadding + 'px' }"
              >
                <div v-if="emailComponents.length === 0" class="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                  <div class="text-gray-400 mb-2">
                    <FileTextIcon class="w-8 h-8 mx-auto" />
                  </div>
                  <h3 class="text-sm font-medium text-gray-900 mb-1">Start building your welcome email</h3>
                  <p class="text-xs text-gray-500">Create a warm first impression with personalized content</p>
                  <p class="text-xs text-blue-600 mt-1">💡 Welcome emails have 4x higher open rates than regular emails!</p>
                </div>

                <!-- Email Components -->
                <div 
                  v-for="(component, index) in emailComponents" 
                  :key="component.id || index" 
                  class="mb-4 relative group"
                >
                  <!-- Component Actions -->
                  <div class="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1" style="z-index: 9999;">
                    <button 
                      v-if="index > 0"
                      @click="moveComponent(index, -1)"
                      class="w-6 h-6 bg-white border border-gray-300 rounded shadow text-gray-600 hover:text-gray-900 flex items-center justify-center"
                      title="Move Up"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                      </svg>
                    </button>
                    <!-- Drag Handle Indicator -->
                    <div 
                      class="w-6 h-6 bg-white border border-gray-300 rounded shadow text-gray-500 flex items-center justify-center cursor-grab drag-handle"
                      title="Drag to move"
                      draggable="true"
                      @dragstart="startDragExistingComponent('main', index, $event)"
                      @dragend="endDragExistingComponent"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
                      </svg>
                    </div>
                    <button 
                      v-if="index < emailComponents.length - 1"
                      @click="moveComponent(index, 1)"
                      class="w-6 h-6 bg-white border border-gray-300 rounded shadow text-gray-600 hover:text-gray-900 flex items-center justify-center"
                      title="Move Down"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </button>
                    <button 
                      @click="duplicateComponent(index)"
                      class="w-6 h-6 bg-white border border-gray-300 rounded shadow text-blue-600 hover:text-blue-700 flex items-center justify-center"
                      title="Duplicate"
                    >
                      <CopyIcon class="w-3 h-3" />
                    </button>
                    <button 
                      @click="deleteComponent(index)"
                      class="w-6 h-6 bg-white border border-gray-300 rounded shadow text-red-600 hover:text-red-700 flex items-center justify-center"
                      title="Delete"
                    >
                      <XIcon class="w-3 h-3" />
                    </button>
                  </div>

                  <!-- Component Click Handler -->
                  <div 
                    @click="selectComponent(index)"
                    :class="[
                      'cursor-pointer transition-all',
                      selectedComponentIndex === index 
                        ? 'ring-2 ring-blue-500 ring-opacity-50' 
                        : ''
                    ]"
                  >
                    <!-- Text Component -->
                    <div v-if="component.type === 'text'" class="p-4 rounded" :style="getComponentStyle(component)">
                      <div 
                        v-if="editingIndex === index"
                        contenteditable="true"
                        ref="textEditor"
                        @blur="stopEditing"
                        @input="updateTextContent(index, $event)"
                        class="outline-none min-h-[40px]"
                        v-html="component.content"
                      ></div>
                      <div 
                        v-else 
                        @dblclick="editComponent(index)" 
                        class="min-h-[40px]" 
                        v-html="component.content || 'Click to edit text'"
                      ></div>
                    </div>

                    <!-- Heading Component -->
                    <div v-else-if="component.type === 'heading'" class="p-4 rounded" :style="getComponentStyle(component)">
                      <div 
                        v-if="editingIndex === index"
                        contenteditable="true"
                        ref="headingEditor"
                        @blur="stopEditing"
                        @input="updateTextContent(index, $event)"
                        class="outline-none min-h-[40px] text-2xl font-bold"
                        v-html="component.content"
                      ></div>
                      <div 
                        v-else 
                        @dblclick="editComponent(index)" 
                        class="min-h-[40px] text-2xl font-bold" 
                        v-html="component.content || 'Click to edit heading'"
                      ></div>
                    </div>

                    <!-- Button Component -->
                    <div v-else-if="component.type === 'button'" class="p-4 text-center" :style="getComponentStyle(component)">
                      <div v-if="editingIndex === index" class="space-y-2">
                        <input 
                          v-model="component.text"
                          @blur="stopEditing"
                          @keyup.enter="stopEditing"
                          class="w-full p-2 border border-gray-300 rounded text-sm"
                          placeholder="Button text"
                        />
                        <input 
                          v-model="component.url"
                          @blur="stopEditing"
                          class="w-full p-2 border border-gray-300 rounded text-sm"
                          placeholder="https://example.com"
                        />
                      </div>
                      <div v-else @dblclick="editComponent(index)">
                        <a 
                          :href="component.url || '#'"
                          :style="getButtonStyle(component)"
                          class="inline-block font-medium py-3 px-6 rounded transition-colors"
                          target="_blank"
                        >
                          {{ component.text || 'Click Here' }}
                        </a>
                      </div>
                    </div>

                    <!-- Image Component -->
                    <div v-else-if="component.type === 'image'" class="p-4 text-center" :style="getComponentStyle(component)">
                      <div v-if="editingIndex === index" class="space-y-2">
                        <input 
                          v-model="component.src"
                          @blur="stopEditing"
                          @keyup.enter="stopEditing"
                          class="w-full p-2 border border-gray-300 rounded text-sm"
                          placeholder="https://example.com/image.jpg"
                        />
                        <input 
                          v-model="component.alt"
                          @blur="stopEditing"
                          class="w-full p-2 border border-gray-300 rounded text-sm"
                          placeholder="Describe the image"
                        />
                      </div>
                      <div v-else @dblclick="editComponent(index)">
                        <img 
                          v-if="component.src" 
                          :src="component.src" 
                          :alt="component.alt" 
                          class="max-w-full h-auto mx-auto rounded" 
                        />
                        <div v-else class="bg-gray-100 h-32 flex items-center justify-center text-gray-500 rounded">
                          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    <!-- Spacer Component -->
                    <div v-else-if="component.type === 'spacer'" class="flex items-center justify-center text-gray-400 text-xs" :style="{ height: (component.height || 40) + 'px' }">
                      {{ component.height || 40 }}px spacer
                    </div>

                    <!-- Divider Component -->
                    <div v-else-if="component.type === 'divider'" class="py-4" :style="getComponentStyle(component)">
                      <hr class="border-gray-300" />
                    </div>

                    <!-- Social Media Component -->
                    <div v-else-if="component.type === 'social'" class="p-4" :style="getComponentStyle(component)">
                      <div :style="{ textAlign: component.socialStyle?.alignment || 'center' }">
                        <div class="inline-flex items-center" :style="{ gap: (component.socialStyle?.spacing || 10) + 'px' }">
                          <!-- Facebook -->
                          <a 
                            v-if="component.links?.facebook" 
                            :href="component.links.facebook"
                            class="inline-flex items-center justify-center bg-blue-600 text-white font-bold text-decoration-none hover:opacity-80 transition-opacity"
                            :style="getSocialButtonStyle(component.socialStyle)"
                            target="_blank"
                            rel="noopener noreferrer">
                            
                          </a>
                          
                          <!-- Instagram -->
                          <a 
                            v-if="component.links?.instagram" 
                            :href="component.links.instagram"
                            class="inline-flex items-center justify-center text-white font-bold text-decoration-none hover:opacity-80 transition-opacity"
                            :style="{ ...getSocialButtonStyle(component.socialStyle), background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)' }"
                            target="_blank"
                            rel="noopener noreferrer">
                            
                          </a>
                          
                          <!-- LinkedIn -->
                          <a 
                            v-if="component.links?.linkedin" 
                            :href="component.links.linkedin"
                            class="inline-flex items-center justify-center bg-blue-700 text-white font-bold text-decoration-none hover:opacity-80 transition-opacity"
                            :style="getSocialButtonStyle(component.socialStyle)"
                            target="_blank"
                            rel="noopener noreferrer">
                            
                          </a>
                          
                          <!-- Twitter -->
                          <a 
                            v-if="component.links?.twitter" 
                            :href="component.links.twitter"
                            class="inline-flex items-center justify-center bg-blue-400 text-white font-bold text-decoration-none hover:opacity-80 transition-opacity"
                            :style="getSocialButtonStyle(component.socialStyle)"
                            target="_blank"
                            rel="noopener noreferrer">
                            
                          </a>
                          
                          <!-- YouTube -->
                          <a 
                            v-if="component.links?.youtube" 
                            :href="component.links.youtube"
                            class="inline-flex items-center justify-center bg-red-600 text-white font-bold text-decoration-none hover:opacity-80 transition-opacity"
                            :style="getSocialButtonStyle(component.socialStyle)"
                            target="_blank"
                            rel="noopener noreferrer">
                            ▶
                          </a>
                          
                          <!-- TikTok -->
                          <a 
                            v-if="component.links?.tiktok" 
                            :href="component.links.tiktok"
                            class="inline-flex items-center justify-center bg-black text-white font-bold text-decoration-none hover:opacity-80 transition-opacity"
                            :style="getSocialButtonStyle(component.socialStyle)"
                            target="_blank"
                            rel="noopener noreferrer">
                            TT
                          </a>
                        </div>
                      </div>
                    </div>

                    <!-- Hero Component -->
                    <div v-else-if="component.type === 'hero'" class="relative overflow-hidden rounded" :style="getHeroStyle(component)">
                      <!-- Background Image/Gradient -->
                      <div 
                        class="absolute inset-0"
                        :style="getHeroBackgroundStyle(component)"
                      ></div>
                      
                      <!-- Content Overlay -->
                      <div class="relative z-10 h-full" :style="getHeroContentStyle(component)">
                        <!-- Text Content Container -->
                        <div>
                          <!-- Main Heading -->
                          <h2 
                            v-if="component.title"
                            class="font-bold mb-4"
                            :style="getHeroTitleStyle(component)"
                          >
                            {{ component.title }}
                          </h2>
                          
                          <!-- Subtitle -->
                          <p 
                            v-if="component.subtitle"
                            class="mb-4"
                            :style="getHeroSubtitleStyle(component)"
                          >
                            {{ component.subtitle }}
                          </p>
                          
                          <!-- Description -->
                          <p 
                            v-if="component.description"
                            class="mb-6"
                            :style="getHeroDescriptionStyle(component)"
                          >
                            {{ component.description }}
                          </p>
                          
                          <!-- CTA Button -->
                          <a 
                            v-if="component.buttonText"
                            :href="component.buttonUrl || '#'"
                            class="inline-block px-6 py-3 rounded font-medium transition-colors text-decoration-none"
                            :style="getHeroButtonStyle(component)"
                            target="_blank"
                          >
                            {{ component.buttonText }}
                          </a>
                        </div>
                      </div>
                    </div>

                    <!-- Columns Component -->
                    <div v-else-if="component.type === 'columns'" class="p-4" :style="getComponentStyle(component)">
                      <div 
                        :class="`grid gap-${component.gap || 4} grid-cols-${component.columnCount || 2}`"
                        :style="{ textAlign: component.alignment || 'left' }"
                      >
                        <div 
                          v-for="(column, colIndex) in component.columns" 
                          :key="colIndex"
                          class="border border-dashed border-gray-300 rounded-lg p-4 min-h-[120px] column-drop-zone relative"
                          @drop="onColumnDrop($event, index, colIndex)"
                          @dragover="onDragOver($event)"
                          @dragenter="onDragEnter($event)"
                          @dragleave="onDragLeave($event)"
                        >
                          <!-- Column Components -->
                          <div v-if="column.components && column.components.length > 0">
                            <div 
                              v-for="(colComponent, colCompIndex) in column.components" 
                              :key="colComponent.id || `col-${colCompIndex}`" 
                              class="mb-3 relative group"
                            >
                              <!-- Column Component Actions -->
                              <div class="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1 z-20">
                                <!-- Drag Handle for Column Components -->
                                <div 
                                  class="w-4 h-4 bg-gray-600 text-white rounded-full flex items-center justify-center cursor-grab drag-handle"
                                  title="Drag to move"
                                  draggable="true"
                                  @dragstart="startDragExistingComponent('column', { parentIndex: index, columnIndex: colIndex, componentIndex: colCompIndex }, $event)"
                                  @dragend="endDragExistingComponent"
                                >
                                  <svg class="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M4 8h16M4 16h16"/>
                                  </svg>
                                </div>
                                <button 
                                  @click="selectColumnComponent(index, colIndex, colCompIndex)"
                                  class="w-5 h-5 bg-blue-600 text-white rounded-full hover:bg-blue-700 flex items-center justify-center"
                                  title="Edit"
                                >
                                  <EditIcon class="w-2 h-2" />
                                </button>
                                <button 
                                  @click="duplicateColumnComponent(index, colIndex, colCompIndex)"
                                  class="w-5 h-5 bg-green-600 text-white rounded-full hover:bg-green-700 flex items-center justify-center"
                                  title="Duplicate"
                                >
                                  <CopyIcon class="w-2 h-2" />
                                </button>
                                <button 
                                  @click="deleteColumnComponent(index, colIndex, colCompIndex)"
                                  class="w-5 h-5 bg-red-600 text-white rounded-full hover:bg-red-700 flex items-center justify-center"
                                  title="Delete"
                                >
                                  <XIcon class="w-2 h-2" />
                                </button>
                              </div>

                              <!-- Render Column Component -->
                              <div 
                                @click.stop="selectColumnComponent(index, colIndex, colCompIndex)"
                                :class="[
                                  'cursor-pointer transition-all rounded',
                                  isColumnComponentSelected(index, colIndex, colCompIndex) 
                                    ? 'ring-2 ring-blue-500 ring-opacity-50' 
                                    : ''
                                ]"
                              >
                                <div v-if="colComponent.type === 'text'" class="text-sm p-2" :style="getColumnComponentStyle(colComponent)">
                                  <div v-html="colComponent.content"></div>
                                </div>
                                <div v-else-if="colComponent.type === 'heading'" class="p-2" :style="getColumnComponentStyle(colComponent)">
                                  <div v-html="colComponent.content" class="text-lg font-bold"></div>
                                </div>
                                <div v-else-if="colComponent.type === 'image'" class="text-center p-2" :style="getColumnComponentStyle(colComponent)">
                                  <img v-if="colComponent.src" :src="colComponent.src" :alt="colComponent.alt" class="max-w-full h-auto rounded" />
                                  <div v-else class="bg-gray-100 h-16 flex items-center justify-center text-gray-500 rounded text-xs">
                                    Image
                                  </div>
                                </div>
                                <div v-else-if="colComponent.type === 'button'" class="text-center p-2" :style="getColumnComponentStyle(colComponent)">
                                  <a 
                                    :href="colComponent.url || '#'" 
                                    :style="getButtonStyle(colComponent)"
                                    class="inline-block px-3 py-2 rounded text-sm font-medium"
                                    target="_blank"
                                  >
                                    {{ colComponent.text || 'Button' }}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Empty Column State -->
                          <div v-else class="text-center text-gray-500 text-xs flex flex-col items-center justify-center h-full">
                            <svg class="w-6 h-6 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                            </svg>
                            <div>Column {{ colIndex + 1 }}</div>
                            <div class="text-xs text-gray-400 mt-1">Drag components here</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email Footer -->
              <div 
                class="bg-gray-50 text-center border-t border-gray-200 text-sm text-gray-600"
                :style="{ padding: emailSettings.containerPadding + 'px' }"
              >
                <p>{{ emailSettings.footerText || (accountData && accountData[0] ? (accountData[0].name + (accountData[0].address ? ' | ' + accountData[0].address : ' | Your trusted automotive partner')) : 'Your Dealership | Contact us for more information') }}</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Right Sidebar - Properties Panel -->
      <div class="w-80 bg-white border-l border-gray-200 overflow-y-auto" style="max-height: calc(85vh - 140px);">
        <div class="p-4">
          <!-- Canvas Settings -->
          <div class="mb-6 pb-6 border-b border-gray-200 canvas-settings">
            <h3 class="text-sm font-medium text-gray-900 uppercase tracking-wide mb-4">
              Canvas Settings
            </h3>
            
            <!-- Canvas Border Radius -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Canvas border radius</label>
              <div class="flex items-center space-x-3">
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <input 
                  type="range"
                  v-model.number="emailSettings.borderRadius"
                  @input="updateCanvasSettings"
                  min="0"
                  max="50"
                  class="flex-1"
                />
                <span class="text-sm font-medium text-gray-900 w-8">{{ emailSettings.borderRadius }}</span>
                <span class="text-xs text-gray-500">px</span>
              </div>
            </div>

            <!-- Header Color -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Header color</label>
              <div class="flex items-center space-x-2">
                <div class="w-8 h-8 rounded border border-gray-300 overflow-hidden">
                  <input 
                    type="color" 
                    v-model="emailSettings.headerColor"
                    @input="updateCanvasSettings"
                    class="w-full h-full border-none cursor-pointer"
                  />
                </div>
                <input 
                  v-model="emailSettings.headerColor"
                  @input="updateCanvasSettings"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="#1f2937"
                />
              </div>
            </div>

            <!-- Background Color -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Background color</label>
              <div class="flex items-center space-x-2">
                <div class="w-8 h-8 rounded border border-gray-300 overflow-hidden">
                  <input 
                    type="color" 
                    v-model="emailSettings.backgroundColor"
                    @input="updateCanvasSettings"
                    class="w-full h-full border-none cursor-pointer"
                  />
                </div>
                <input 
                  v-model="emailSettings.backgroundColor"
                  @input="updateCanvasSettings"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="#ffffff"
                />
              </div>
            </div>

            <!-- Container Padding -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Container padding</label>
              <div class="flex items-center space-x-3">
                <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
                <input 
                  type="range"
                  v-model.number="emailSettings.containerPadding"
                  @input="updateCanvasSettings"
                  min="10"
                  max="60"
                  class="flex-1"
                />
                <span class="text-sm font-medium text-gray-900 w-8">{{ emailSettings.containerPadding }}</span>
                <span class="text-xs text-gray-500">px</span>
              </div>
            </div>

            <!-- Header Settings -->
            <div class="mb-4">
              <h4 class="text-sm font-medium text-gray-900 mb-3">Header Settings</h4>
              
              <!-- Show Header Toggle -->
              <div class="mb-3">
                <label class="flex items-center">
                  <input 
                    type="checkbox"
                    v-model="emailSettings.showHeader"
                    @change="updateCanvasSettings"
                    class="mr-2"
                  />
                  <span class="text-sm text-gray-700">Show email header</span>
                </label>
                <div class="text-xs text-gray-500 mt-1">Include branded header with logo and greeting</div>
              </div>
              
              <!-- Logo Text -->
              <div class="mb-3" v-if="emailSettings.showHeader">
                <label class="block text-sm font-medium text-gray-700 mb-2">Logo text</label>
                <input 
                  v-model="emailSettings.logoText"
                  @input="updateCanvasSettings"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  :placeholder="accountData && accountData[0] && accountData[0].name ? generateLogoText(accountData[0].name) : 'FG'"
                  maxlength="4"
                />
                <div class="text-xs text-gray-500 mt-1">Keep it short (1-4 characters)</div>
              </div>

              <!-- Header Title -->
              <div class="mb-3" v-if="emailSettings.showHeader">
                <label class="block text-sm font-medium text-gray-700 mb-2">Header title</label>
                <input 
                  v-model="emailSettings.headerTitle"
                  @input="updateCanvasSettings"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Welcome!"
                />
                <div class="text-xs text-gray-500 mt-1">Personalized greeting or title</div>
              </div>

              <!-- Email Subject -->
              <div class="mb-3">
                <label class="block text-sm font-medium text-gray-700 mb-2">Email subject</label>
                <input 
                  v-model="emailSettings.subject"
                  @input="updateCanvasSettings"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Welcome! Your Journey Starts Here"
                />
                <div class="text-xs text-gray-500 mt-1">Subject line for the email</div>
              </div>
            </div>

            <!-- Footer Settings -->
            <div class="mb-4">
              <h4 class="text-sm font-medium text-gray-900 mb-3">Footer Settings</h4>
              
              <!-- Footer Text -->
              <div class="mb-3">
                <label class="block text-sm font-medium text-gray-700 mb-2">Footer text</label>
                <textarea 
                  v-model="emailSettings.footerText"
                  @input="updateCanvasSettings"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  rows="2"
                  :placeholder="accountData && accountData[0] ? (accountData[0].name + (accountData[0].address ? ' | ' + accountData[0].address : ' | Your trusted automotive partner')) : 'Your Dealership | Contact information'"
                ></textarea>
                <div class="text-xs text-gray-500 mt-1">Company info and contact details</div>
              </div>
            </div>
          </div>



          <!-- Block Type Header -->
          <div v-if="selectedComponent" class="mb-6">
            <h3 class="text-sm font-medium text-gray-900 uppercase tracking-wide mb-4">
              {{ getBlockTypeName(selectedComponent.type) }}
              <span v-if="selectedColumnComponent" class="text-xs text-blue-600 normal-case ml-2">(in column)</span>
            </h3>
            
            <!-- Text Block Properties -->
            <div v-if="selectedComponent.type === 'text' || selectedComponent.type === 'heading'">
              <!-- Content -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Content</label>
                <div class="border border-gray-300 rounded-md">
                  <!-- Rich Text Toolbar -->
                  <div class="border-b border-gray-300 p-2 flex flex-wrap items-center gap-1 bg-gray-50 rich-text-toolbar">
                    <!-- Text Formatting -->
                    <div class="flex items-center border-r border-gray-300 pr-2 mr-2">
                      <button 
                        @click="applyTextFormat('bold')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Bold"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 3h5.5c1.5 0 2.5 1 2.5 2.5S12 8 10.5 8H5V3zm0 7h6c1.5 0 2.5 1 2.5 2.5S12.5 15 11 15H5v-5z"/>
                        </svg>
                      </button>
                      <button 
                        @click="applyTextFormat('italic')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Italic"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8 3h4v2H9.5L7.5 15H10v2H6v-2h2.5L10.5 5H8V3z"/>
                        </svg>
                      </button>
                      <button 
                        @click="applyTextFormat('underline')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Underline"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2C8 2 6 4 6 6v4c0 2 2 4 4 4s4-2 4-4V6c0-2-2-4-4-4zm0 10c-1 0-2-1-2-2V6c0-1 1-2 2-2s2 1 2 2v4c0 1-1 2-2 2zM4 18h12v1H4z"/>
                        </svg>
                      </button>
                    </div>

                    <!-- Text Alignment -->
                    <div class="flex items-center border-r border-gray-300 pr-2 mr-2">
                      <button 
                        @click="applyTextFormat('justifyLeft')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Align Left"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4h14v2H3V4zm0 4h10v2H3V8zm0 4h14v2H3v-2zm0 4h10v2H3v-2z"/>
                        </svg>
                      </button>
                      <button 
                        @click="applyTextFormat('justifyCenter')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Align Center"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4h14v2H3V4zm2 4h10v2H5V8zm-2 4h14v2H3v-2zm2 4h10v2H5v-2z"/>
                        </svg>
                      </button>
                      <button 
                        @click="applyTextFormat('justifyRight')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Align Right"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4h14v2H3V4zm4 4h10v2H7V8zm-4 4h14v2H3v-2zm4 4h10v2H7v-2z"/>
                        </svg>
                      </button>
                    </div>

                    <!-- Lists -->
                    <div class="flex items-center border-r border-gray-300 pr-2 mr-2">
                      <button 
                        @click="applyTextFormat('insertUnorderedList')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Bullet List"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 7a1 1 0 100-2 1 1 0 000 2zM3 11a1 1 0 100-2 1 1 0 000 2zM3 15a1 1 0 100-2 1 1 0 000 2zM6 6h11v2H6V6zM6 10h11v2H6v-2zM6 14h11v2H6v-2z"/>
                        </svg>
                      </button>
                      <button 
                        @click="applyTextFormat('insertOrderedList')"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Numbered List"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 8a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 12a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 16a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"/>
                        </svg>
                      </button>
                    </div>

                    <!-- Font Size -->
                    <div class="flex items-center border-r border-gray-300 pr-2 mr-2">
                      <select 
                        @change="applyFontSize($event.target.value)"
                        class="text-xs border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="12">12px</option>
                        <option value="14" selected>14px</option>
                        <option value="16">16px</option>
                        <option value="18">18px</option>
                        <option value="20">20px</option>
                        <option value="24">24px</option>
                        <option value="28">28px</option>
                        <option value="32">32px</option>
                      </select>
                    </div>

                    <!-- Text Color -->
                    <div class="flex items-center border-r border-gray-300 pr-2 mr-2">
                      <input 
                        type="color" 
                        @change="applyTextColor($event.target.value)"
                        class="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                        title="Text Color"
                      />
                    </div>

                    <!-- Additional Tools -->
                    <div class="flex items-center">
                      <button 
                        @click="insertLink"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600 mr-1"
                        title="Insert Link"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"/>
                        </svg>
                      </button>
                      <button 
                        @click="clearFormatting"
                        class="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                        title="Clear Formatting"
                      >
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.5 3.5A1.5 1.5 0 0010 5v.5a3.5 3.5 0 017 0V9a1 1 0 11-2 0V5.5a1.5 1.5 0 00-3 0V9a1 1 0 01-2 0V5a3.5 3.5 0 00-1.5-2.86V3.5z"/>
                          <path d="M3 13.5a1.5 1.5 0 003 0V12a1 1 0 112 0v1.5a3.5 3.5 0 107 0V12a1 1 0 112 0v1.5a1.5 1.5 0 003 0V12a3.5 3.5 0 00-7-0v1.5a1.5 1.5 0 01-3 0V12a3.5 3.5 0 00-7 0v1.5z"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Rich Text Editor -->
                  <div
                    ref="richTextEditor"
                    contenteditable="true"
                    @input="updateRichTextContent"
                    @blur="updateRichTextContent"
                    @keydown="handleKeyDown"
                    class="w-full p-3 outline-none resize-none rich-text-editor"
                    :data-placeholder="selectedComponent.type === 'heading' ? 'Enter heading text...' : 'Enter your text content...'"
                    v-html="selectedComponent.content"
                  ></div>
                  
                  <!-- Keyboard Shortcuts Help -->
                  <div class="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
                    <div class="mb-1">
                      <strong>Shortcuts:</strong> Ctrl+B (Bold), Ctrl+I (Italic), Ctrl+U (Underline), Ctrl+K (Link), Ctrl+L (Left), Ctrl+E (Center), Ctrl+R (Right), Ctrl+D (Duplicate)
                    </div>
                    <div>
                      <strong>Drag & Drop:</strong> Use the drag handles (≡) to move components between areas
                    </div>
                  </div>
                </div>
              </div>

              <!-- Text Color -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Text color</label>
                <div class="flex items-center space-x-2">
                  <div class="w-8 h-8 rounded border border-gray-300 overflow-hidden">
                    <input 
                      type="color" 
                      v-model="selectedComponent.style.color"
                      @input="updateComponentStyle"
                      class="w-full h-full border-none cursor-pointer"
                    />
                  </div>
                  <input 
                    v-model="selectedComponent.style.color"
                    @input="updateComponentStyle"
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <!-- Background Color -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Background color</label>
                <div class="flex items-center space-x-2">
                  <div class="w-8 h-8 rounded border border-gray-300 overflow-hidden">
                    <input 
                      type="color" 
                      v-model="selectedComponent.style.backgroundColor"
                      @input="updateComponentStyle"
                      class="w-full h-full border-none cursor-pointer"
                    />
                  </div>
                  <input 
                    v-model="selectedComponent.style.backgroundColor"
                    @input="updateComponentStyle"
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="#ffffff"
                  />
                </div>
              </div>

              <!-- Font Family -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Font family</label>
                <select 
                  v-model="selectedComponent.style.fontFamily"
                  @change="updateComponentStyle"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Match email settings</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Times New Roman, serif">Times New Roman</option>
                  <option value="Helvetica, sans-serif">Helvetica</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                </select>
              </div>

              <!-- Font Size -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Font size</label>
                <div class="flex items-center space-x-3">
                  <span class="text-sm text-gray-500">Aa</span>
                  <input 
                    type="range"
                    v-model.number="selectedComponent.style.fontSize"
                    @input="updateComponentStyle"
                    min="12"
                    max="48"
                    class="flex-1"
                  />
                  <span class="text-sm font-medium text-gray-900 w-8">{{ selectedComponent.style.fontSize || 16 }}</span>
                  <span class="text-xs text-gray-500">px</span>
                </div>
              </div>

              <!-- Font Weight -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Font weight</label>
                <div class="flex rounded-md overflow-hidden border border-gray-300">
                  <button 
                    @click="setFontWeight('normal')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm',
                      selectedComponent.style.fontWeight === 'normal' || !selectedComponent.style.fontWeight
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    Regular
                  </button>
                  <button 
                    @click="setFontWeight('bold')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm border-l border-gray-300',
                      selectedComponent.style.fontWeight === 'bold'
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    Bold
                  </button>
                </div>
              </div>

              <!-- Alignment -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
                <div class="flex rounded-md overflow-hidden border border-gray-300">
                  <button 
                    @click="setAlignment('left')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm flex items-center justify-center',
                      selectedComponent.style.textAlign === 'left' || !selectedComponent.style.textAlign
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h8M4 18h12"/>
                    </svg>
                  </button>
                  <button 
                    @click="setAlignment('center')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm border-l border-gray-300 flex items-center justify-center',
                      selectedComponent.style.textAlign === 'center'
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M8 12h8M6 18h12"/>
                    </svg>
                  </button>
                  <button 
                    @click="setAlignment('right')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm border-l border-gray-300 flex items-center justify-center',
                      selectedComponent.style.textAlign === 'right'
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M12 12h8M10 18h10"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Padding -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Padding</label>
                <div class="space-y-2">
                  <!-- Top/Bottom Padding -->
                  <div class="flex items-center space-x-3">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/>
                    </svg>
                    <input 
                      type="range"
                      v-model.number="paddingTop"
                      @input="updatePadding"
                      min="0"
                      max="80"
                      class="flex-1"
                    />
                    <span class="text-sm font-medium text-gray-900 w-8">{{ paddingTop }}</span>
                    <span class="text-xs text-gray-500">px</span>
                  </div>
                  <!-- Left/Right Padding -->
                  <div class="flex items-center space-x-3">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18m0 0l-4-4m4 4l4-4"/>
                    </svg>
                    <input 
                      type="range"
                      v-model.number="paddingLeft"
                      @input="updatePadding"
                      min="0"
                      max="80"
                      class="flex-1"
                    />
                    <span class="text-sm font-medium text-gray-900 w-8">{{ paddingLeft }}</span>
                    <span class="text-xs text-gray-500">px</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Button Block Properties -->
            <div v-else-if="selectedComponent.type === 'button'">
              <!-- Button Text -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Text</label>
                <input 
                  v-model="selectedComponent.text"
                  @input="updateComponentContent"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Button text"
                />
              </div>

              <!-- Button URL -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">URL</label>
                <input 
                  v-model="selectedComponent.url"
                  @input="updateComponentContent"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="https://example.com"
                />
              </div>

              <!-- Button Style -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Style</label>
                <div class="flex rounded-md overflow-hidden border border-gray-300">
                  <button 
                    @click="setButtonStyle('filled')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm',
                      selectedComponent.buttonStyle === 'filled' || !selectedComponent.buttonStyle
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    Filled
                  </button>
                  <button 
                    @click="setButtonStyle('outline')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm border-l border-gray-300',
                      selectedComponent.buttonStyle === 'outline'
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    Outline
                  </button>
                </div>
              </div>

              <!-- Button Color -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Button color</label>
                <div class="flex items-center space-x-2">
                  <div class="w-8 h-8 rounded border border-gray-300 overflow-hidden">
                    <input 
                      type="color" 
                      v-model="selectedComponent.buttonColor"
                      @input="updateComponentContent"
                      class="w-full h-full border-none cursor-pointer"
                    />
                  </div>
                  <input 
                    v-model="selectedComponent.buttonColor"
                    @input="updateComponentContent"
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="#3b82f6"
                  />
                </div>
              </div>
            </div>

            <!-- Image Block Properties -->
            <div v-else-if="selectedComponent.type === 'image'">
              <!-- Image URL -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                <input 
                  v-model="selectedComponent.src"
                  @input="updateComponentContent"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <!-- Alt Text -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Alt text</label>
                <input 
                  v-model="selectedComponent.alt"
                  @input="updateComponentContent"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Describe the image"
                />
              </div>
            </div>

            <!-- Spacer Block Properties -->
            <div v-else-if="selectedComponent.type === 'spacer'">
              <!-- Height -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Height</label>
                <div class="flex items-center space-x-3">
                  <span class="text-sm text-gray-500">0</span>
                  <input 
                    type="range"
                    v-model.number="selectedComponent.height"
                    @input="updateComponentContent"
                    min="10"
                    max="200"
                    class="flex-1"
                  />
                  <span class="text-sm font-medium text-gray-900 w-12">{{ selectedComponent.height || 40 }}</span>
                  <span class="text-xs text-gray-500">px</span>
                </div>
              </div>
            </div>

            <!-- Columns Block Properties -->
            <div v-else-if="selectedComponent.type === 'columns'">
              <!-- Number of columns -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Number of columns</label>
                <div class="flex rounded-md overflow-hidden border border-gray-300">
                  <button 
                    @click="setColumnCount(2)"
                    :class="[
                      'flex-1 px-3 py-2 text-sm',
                      selectedComponent.columnCount === 2 || !selectedComponent.columnCount
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    2
                  </button>
                  <button 
                    @click="setColumnCount(3)"
                    :class="[
                      'flex-1 px-3 py-2 text-sm border-l border-gray-300',
                      selectedComponent.columnCount === 3
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    3
                  </button>
                </div>
              </div>

              <!-- Column widths -->
              <div class="mb-4">
                <div class="grid grid-cols-3 gap-2">
                  <div v-for="(column, index) in selectedComponent.columns" :key="index">
                    <label class="block text-xs font-medium text-gray-700 mb-1">Column {{ index + 1 }}</label>
                    <div class="flex items-center space-x-1">
                      <input 
                        v-model="column.width"
                        @input="updateComponentContent"
                        class="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                        placeholder="auto"
                      />
                      <span class="text-xs text-gray-500">px</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Columns gap -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Columns gap</label>
                <div class="flex items-center space-x-3">
                  <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18"/>
                  </svg>
                  <input 
                    type="range"
                    v-model.number="selectedComponent.gap"
                    @input="updateComponentContent"
                    min="0"
                    max="40"
                    class="flex-1"
                  />
                  <span class="text-sm font-medium text-gray-900 w-8">{{ selectedComponent.gap || 16 }}</span>
                  <span class="text-xs text-gray-500">px</span>
                </div>
              </div>

              <!-- Alignment -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Alignment</label>
                <div class="flex rounded-md overflow-hidden border border-gray-300">
                  <button 
                    @click="setColumnAlignment('left')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm flex items-center justify-center',
                      selectedComponent.alignment === 'left' || !selectedComponent.alignment
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h8M4 18h12"/>
                    </svg>
                  </button>
                  <button 
                    @click="setColumnAlignment('center')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm border-l border-gray-300 flex items-center justify-center',
                      selectedComponent.alignment === 'center'
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M8 12h8M6 18h12"/>
                    </svg>
                  </button>
                  <button 
                    @click="setColumnAlignment('right')"
                    :class="[
                      'flex-1 px-3 py-2 text-sm border-l border-gray-300 flex items-center justify-center',
                      selectedComponent.alignment === 'right'
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    ]"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M12 12h8M10 18h10"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Background color -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Background color</label>
                <div class="flex items-center space-x-2">
                  <div class="w-8 h-8 rounded border border-gray-300 overflow-hidden">
                    <input 
                      type="color" 
                      v-model="selectedComponent.style.backgroundColor"
                      @input="updateComponentStyle"
                      class="w-full h-full border-none cursor-pointer"
                    />
                  </div>
                  <input 
                    v-model="selectedComponent.style.backgroundColor"
                    @input="updateComponentStyle"
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="#ffffff"
                  />
                </div>
              </div>

              <!-- Padding -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Padding</label>
                <div class="space-y-2">
                  <!-- Top/Bottom Padding -->
                  <div class="flex items-center space-x-3">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/>
                    </svg>
                    <input 
                      type="range"
                      v-model.number="paddingTop"
                      @input="updatePadding"
                      min="0"
                      max="80"
                      class="flex-1"
                    />
                    <span class="text-sm font-medium text-gray-900 w-8">{{ paddingTop }}</span>
                    <span class="text-xs text-gray-500">px</span>
                  </div>
                  <!-- Left/Right Padding -->
                  <div class="flex items-center space-x-3">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18m0 0l-4-4m4 4l4-4"/>
                    </svg>
                    <input 
                      type="range"
                      v-model.number="paddingLeft"
                      @input="updatePadding"
                      min="0"
                      max="80"
                      class="flex-1"
                    />
                    <span class="text-sm font-medium text-gray-900 w-8">{{ paddingLeft }}</span>
                    <span class="text-xs text-gray-500">px</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Hero Block Properties -->
            <div v-else-if="selectedComponent.type === 'hero'">
              <!-- Content Text Fields -->
              <div class="mb-6">
                <h4 class="text-sm font-medium text-gray-900 mb-3">Content</h4>
                
                <!-- Quick Templates -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Quick Templates</label>
                  <div class="grid grid-cols-2 gap-2">
                    <button 
                      @click="applyHeroTemplate('marketing')"
                      class="p-3 text-xs bg-gradient-to-r from-blue-500 to-pink-500 text-white rounded hover:from-blue-600 hover:to-pink-600"
                    >
                      Marketing
                    </button>
                    <button 
                      @click="applyHeroTemplate('announcement')"
                      class="p-3 text-xs bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded hover:from-blue-600 hover:to-cyan-600"
                    >
                      📢 Announcement
                    </button>
                    <button 
                      @click="applyHeroTemplate('product')"
                      class="p-3 text-xs bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded hover:from-green-600 hover:to-emerald-600"
                    >
                      Product Launch
                    </button>
                    <button 
                      @click="applyHeroTemplate('dealership')"
                      class="p-3 text-xs bg-gradient-to-r from-gray-600 to-gray-800 text-white rounded hover:from-gray-700 hover:to-gray-900"
                    >
                      🏢 Dealership
                    </button>
                  </div>
                </div>
                
                <!-- Title -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input 
                    v-model="selectedComponent.title"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Main heading"
                  />
                </div>

                <!-- Subtitle -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                  <input 
                    v-model="selectedComponent.subtitle"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Secondary heading"
                  />
                </div>

                <!-- Description -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea 
                    v-model="selectedComponent.description"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    rows="3"
                    placeholder="Description text"
                  ></textarea>
                </div>

                <!-- Button Text -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                  <input 
                    v-model="selectedComponent.buttonText"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Call to action"
                  />
                </div>

                <!-- Button URL -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Button URL</label>
                  <input 
                    v-model="selectedComponent.buttonUrl"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    :placeholder="accountData && accountData[0] && accountData[0].website ? accountData[0].website : 'https://example.com'"
                  />
                </div>
              </div>

              <!-- Background Settings -->
              <div class="mb-6">
                <h4 class="text-sm font-medium text-gray-900 mb-3">Background</h4>
                
                <!-- Background Image URL -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                  <input 
                    v-model="selectedComponent.backgroundImage"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="https://example.com/image.jpg"
                  />
                  <div class="text-xs text-gray-500 mt-1">
                    Try: Unsplash, Pexels, or your own image URLs. Image will override gradient.
                  </div>
                </div>

                <!-- Gradient -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Gradient (if no image)</label>
                  <input 
                    v-model="selectedComponent.backgroundGradient"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="linear-gradient(135deg, #7C3AED 0%, #EC4899 50%, #8B5CF6 100%)"
                  />
                </div>

                <!-- Overlay Toggle -->
                <div class="mb-3">
                  <label class="flex items-center">
                    <input 
                      type="checkbox"
                      v-model="selectedComponent.overlay"
                      @change="updateComponentContent"
                      class="mr-2"
                    />
                    <span class="text-sm text-gray-700">Add overlay</span>
                  </label>
                </div>

                <!-- Overlay Color -->
                <div v-if="selectedComponent.overlay" class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Overlay Color</label>
                  <div class="flex space-x-2 mb-2">
                    <button 
                      @click="selectedComponent.overlayColor = 'rgba(0,0,0,0.3)'; updateComponentContent()"
                      class="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                    >
                      Dark
                    </button>
                    <button 
                      @click="selectedComponent.overlayColor = 'rgba(0,0,0,0.5)'; updateComponentContent()"
                      class="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                    >
                      Darker
                    </button>
                    <button 
                      @click="selectedComponent.overlayColor = 'rgba(255,255,255,0.3)'; updateComponentContent()"
                      class="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                    >
                      Light
                    </button>
                    <button 
                      @click="selectedComponent.overlayColor = 'rgba(139,92,246,0.4)'; updateComponentContent()"
                      class="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                    >
                      Purple
                    </button>
                  </div>
                  <input 
                    v-model="selectedComponent.overlayColor"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="rgba(0,0,0,0.3)"
                  />
                </div>

                <!-- Height -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Height (px)</label>
                  <input 
                    type="number"
                    v-model.number="selectedComponent.height"
                    @input="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    min="200"
                    max="800"
                  />
                </div>

                <!-- Border Radius -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Border Radius: {{ selectedComponent.borderRadius || 8 }}px
                  </label>
                  <!-- Fixed v-model conflict -->
                  <input 
                    type="range"
                    v-model.number="selectedComponent.borderRadius"
                    @input="updateComponentContent"
                    class="w-full"
                    min="0"
                    max="50"
                  />
                </div>
              </div>

              <!-- Text Positioning -->
              <div class="mb-6">
                <h4 class="text-sm font-medium text-gray-900 mb-3">Text Position</h4>
                
                <!-- Content Position -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Position</label>
                  <select 
                    v-model="selectedComponent.contentPosition"
                    @change="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="center-left">Center Left</option>
                    <option value="center-center">Center Center</option>
                    <option value="center-right">Center Right</option>
                    <option value="top-left">Top Left</option>
                    <option value="top-center">Top Center</option>
                    <option value="top-right">Top Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="bottom-center">Bottom Center</option>
                    <option value="bottom-right">Bottom Right</option>
                  </select>
                </div>

                <!-- Text Alignment -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Text Alignment</label>
                  <select 
                    v-model="selectedComponent.textAlignment"
                    @change="updateComponentContent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>

              <!-- Text Styling -->
              <div class="mb-6">
                <h4 class="text-sm font-medium text-gray-900 mb-3">Text Colors & Sizes</h4>
                
                <!-- Title Styling -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <div class="flex space-x-2">
                    <input 
                      type="color"
                      v-model="selectedComponent.titleColor"
                      @input="updateComponentContent"
                      class="w-10 h-8 rounded border border-gray-300"
                    />
                    <input 
                      type="number"
                      v-model.number="selectedComponent.titleSize"
                      @input="updateComponentContent"
                      class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="36"
                      min="12"
                      max="72"
                    />
                    <span class="text-xs text-gray-500 self-center">px</span>
                  </div>
                </div>

                <!-- Subtitle Styling -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                  <div class="flex space-x-2">
                    <input 
                      type="color"
                      v-model="selectedComponent.subtitleColor"
                      @input="updateComponentContent"
                      class="w-10 h-8 rounded border border-gray-300"
                    />
                    <input 
                      type="number"
                      v-model.number="selectedComponent.subtitleSize"
                      @input="updateComponentContent"
                      class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="24"
                      min="12"
                      max="48"
                    />
                    <span class="text-xs text-gray-500 self-center">px</span>
                  </div>
                </div>

                <!-- Description Styling -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <div class="flex space-x-2">
                    <input 
                      type="color"
                      v-model="selectedComponent.descriptionColor"
                      @input="updateComponentContent"
                      class="w-10 h-8 rounded border border-gray-300"
                    />
                    <input 
                      type="number"
                      v-model.number="selectedComponent.descriptionSize"
                      @input="updateComponentContent"
                      class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="16"
                      min="12"
                      max="24"
                    />
                    <span class="text-xs text-gray-500 self-center">px</span>
                  </div>
                </div>

                <!-- Button Styling -->
                <div class="mb-3">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Button</label>
                  <div class="flex space-x-2 mb-2">
                    <input 
                      type="color"
                      v-model="selectedComponent.buttonColor"
                      @input="updateComponentContent"
                      class="w-10 h-8 rounded border border-gray-300"
                      title="Text Color"
                    />
                    <input 
                      type="color"
                      v-model="selectedComponent.buttonBackgroundColor"
                      @input="updateComponentContent"
                      class="w-10 h-8 rounded border border-gray-300"
                      title="Background Color"
                    />
                  </div>
                  <div class="text-xs text-gray-500">
                    <span class="block">Left: Text color</span>
                    <span class="block">Right: Background color</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Selection State -->
          <div v-else class="text-center py-12">
            <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"/>
            </svg>
            <h3 class="text-sm font-medium text-gray-900 mb-1">Select a component</h3>
            <p class="text-xs text-gray-500">Click on any component in your email to edit its properties</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer - Sticky to bottom -->
    <div class="bg-white border-t border-gray-200 px-4 py-2 flex justify-between items-center mt-auto">
      <div class="flex items-center space-x-3">
        <button 
          @click="showPreview" 
          class="inline-flex items-center px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-md transition-colors duration-200"
        >
          <EyeIcon class="w-4 h-4 mr-1.5" />
          Preview
        </button>
        
        <button 
          @click="exportHtml" 
          class="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50 text-gray-700 text-sm transition-colors duration-200"
        >
          <DownloadIcon class="w-4 h-4 mr-1.5" />
          Export HTML
        </button>
      </div>
      
      <div class="flex items-center space-x-2">
        <button 
          @click="$emit('save', { 
            name: template?.name || 'Untitled Template', 
            content: generateHtml(),
            builderData: {
              emailComponents: JSON.parse(JSON.stringify(emailComponents)),
              emailSettings: { ...emailSettings }
            }
          })"
          class="inline-flex items-center px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
        >
          <SaveIcon class="w-4 h-4 mr-1.5" />
          Save Template
        </button>
        
        <button 
          v-if="showCloseButton"
          @click="$emit('close')"
          class="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50 text-gray-700 text-sm transition-colors duration-200"
        >
          <XIcon class="w-4 h-4 mr-1.5" />
          Close Builder
        </button>
      </div>
    </div>

    <!-- Notification -->
    <div 
      v-if="notification.show" 
      :class="[
        'fixed top-4 right-4 z-50 max-w-sm w-full bg-white border border-gray-200 rounded-lg shadow-lg',
        notification.type === 'success' ? 'border-green-200' : 'border-red-200'
      ]"
    >
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <CheckCircleIcon v-if="notification.type === 'success'" class="w-5 h-5 text-green-400" />
            <XCircleIcon v-else class="w-5 h-5 text-red-400" />
          </div>
          <div class="ml-3 w-0 flex-1">
            <p class="text-sm font-medium text-gray-900">{{ notification.title }}</p>
            <p class="mt-1 text-sm text-gray-500">{{ notification.message }}</p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button @click="hideNotification" class="text-gray-400 hover:text-gray-600">
              <XIcon class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Socials Modal -->
    <SocialsModal
      :show="showSocialsModal"
      @close="showSocialsModal = false"
      @add-social="addSocialComponent"
    />
  </div>
</template>

<script>
import {
  FileTextIcon,
  SaveIcon,
  DownloadIcon,
  CheckCircleIcon,
  XCircleIcon,
  XIcon,
  EditIcon,
  CopyIcon,
  EyeIcon
} from 'vue-feather-icons';
import SocialsModal from './SocialsModal.vue';

export default {
  name: 'AdvancedEmailBuilder',
  components: {
    FileTextIcon,
    SaveIcon,
    DownloadIcon,
    CheckCircleIcon,
    XCircleIcon,
    XIcon,
    EditIcon,
    CopyIcon,
    EyeIcon,
    SocialsModal
  },
  props: {
    showCloseButton: {
      type: Boolean,
      default: false
    },
    template: {
      type: Object,
      default: null
    },
    integrationMode: {
      type: Boolean,
      default: false
    },
    accountData: {
      type: Array,
      default: null
    },
    accountSlug: {
      type: String,
      default: null
    },
    initialSettings: {
      type: Object,
      default: null
    }
  },
  watch: {
    template: {
      handler(newTemplate) {
        if (newTemplate && this.integrationMode) {
          this.loadTemplateData(newTemplate);
        }
      },
      immediate: true
    },
    initialSettings: {
      handler(newSettings) {
        if (newSettings) {
          this.emailSettings = { ...this.emailSettings, ...newSettings };
        }
      },
      immediate: true
    },
    accountData: {
      handler(newAccountData) {
        if (newAccountData && newAccountData[0]) {
          this.updateTemplateLibraryWithAccountData(newAccountData[0]);
          
          // Load account-specific template after updating the library
          this.$nextTick(() => {
            const accountTemplate = this.templateLibrary.find(t => t.id === 'account-specific');
            if (accountTemplate) {
              this.loadTemplate(accountTemplate);
            }
          });
        }
      },
      immediate: true
    },
    selectedComponent: {
      handler(newComponent) {
        if (newComponent && this.$refs.richTextEditor) {
          // Update the rich text editor content when component changes
          this.$nextTick(() => {
            if (this.$refs.richTextEditor) {
              this.$refs.richTextEditor.innerHTML = newComponent.content || '';
            }
          });
        }
      },
      immediate: true
    },
    
    showTagInput(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          if (this.$refs.tagInput) {
            this.$refs.tagInput.focus();
          }
        });
      }
    }
  },
  data() {
    return {
      // Email components in the canvas
      emailComponents: [],
      
      // History for undo/redo
      history: [[]],
      historyIndex: 0,
      
      // Currently editing component index
      editingIndex: -1,
      
      // Currently selected component
      selectedComponentIndex: -1,
      selectedTemplate: null,
      
      // Email settings
      emailSettings: {
        subject: 'Welcome! Your Journey Starts Here',
        headerTitle: 'Welcome!',
        headerColor: '#1e40af',
        logoText: 'AD',
        footerText: 'Your Dealership | Contact us for more information',
        borderRadius: 12,
        backgroundColor: '#ffffff',
        containerPadding: 32,
        showHeader: true
      },
      


      
      // Padding controls
      paddingTop: 16,
      paddingLeft: 16,
      
      // Notification system
      notification: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      },
      
      // Template library (optimized for welcome emails)
      templateLibrary: [
        {
          id: 'empty',
          name: 'Empty Canvas',
          description: 'Start from scratch',
          components: []
        },
        {
          id: 'welcome-basic',
          name: 'Basic Welcome',
          description: 'Simple and warm greeting',
          components: [
            {
              type: 'text',
              content: '<p>Hi <strong>{Buyers Name:1}</strong>,</p><p>Congratulations on your new <strong>{Buyers Vehicle:4}</strong>! We\'re delighted to welcome you to our Honda community.</p>',
              style: { color: '#374151', fontSize: 16, fontFamily: 'Arial, sans-serif', textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>Our priority is to ensure you and your vehicle receive the best care possible. If you have any questions or need support, please don\'t hesitate to contact us. We\'re here for you at every step!</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>We invite you to stay connected with us through our social media accounts. We value building meaningful, long-term relationships with our customers and encourage you to join us online.</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>If you\'ve had a wonderful experience with us, we\'d appreciate it if you could share your thoughts by leaving a review on Google or Facebook. Your feedback is invaluable to us.</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>Thank you once again for choosing <strong>[account]</strong>. Wishing you joyful and safe journeys ahead.</p><p><strong>The [account] Team</strong></p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            }
          ]
        },
        {
          id: 'welcome-complete',
          name: 'Complete Welcome',
          description: 'Full welcome experience with services',
          components: [
            {
              type: 'text',
              content: '<p>Hi <strong>{Buyers Name:1}</strong>,</p><p>Congratulations on your new <strong>{Buyers Vehicle:4}</strong>! We\'re delighted to welcome you to our Honda community.</p>',
              style: { color: '#374151', fontSize: 16, fontFamily: 'Arial, sans-serif', textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>Our priority is to ensure you and your vehicle receive the best care possible. If you have any questions or need support, please don\'t hesitate to contact us. We\'re here for you at every step!</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>We invite you to stay connected with us through our social media accounts. We value building meaningful, long-term relationships with our customers and encourage you to join us online.</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>If you\'ve had a wonderful experience with us, we\'d appreciate it if you could share your thoughts by leaving a review on Google or Facebook. Your feedback is invaluable to us.</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'columns',
              columnCount: 2,
              gap: 4,
              alignment: 'center',
              columns: [
                { 
                  components: [
                    {
                      type: 'text',
                      content: '<h3>Seek Your Accessories</h3><p>ACCESSORIES ENQUIRY</p>',
                      style: { color: '#374151', fontSize: 16, textAlign: 'center', padding: '20px', backgroundColor: '#f8fafc' }
                    },
                    {
                      type: 'button',
                      text: 'ACCESSORIES ENQUIRY',
                      url: 'https://example.com/accessories',
                      buttonColor: '#6b7280',
                      style: { textAlign: 'center', padding: '10px' }
            }
          ]
        },
        {
          components: [
            {
              type: 'text',
                      content: '<h3>Book Your Service</h3><p>BOOK YOUR SERVICE</p>',
                      style: { color: '#374151', fontSize: 16, textAlign: 'center', padding: '20px', backgroundColor: '#f8fafc' }
            },
            {
              type: 'button',
                      text: 'BOOK YOUR SERVICE',
                      url: 'https://example.com/service',
                      buttonColor: '#6b7280',
                      style: { textAlign: 'center', padding: '10px' }
                    }
                  ]
                }
              ],
              style: { padding: '25px' }
            },
            {
              type: 'text',
              content: '<h3>Tell us what you think</h3>',
              style: { color: '#374151', fontSize: 18, textAlign: 'center', padding: '20px 25px 10px' }
            },
            {
              type: 'columns',
              columnCount: 2,
              gap: 4,
              alignment: 'center',
              columns: [
                { 
                  components: [
                    {
                      type: 'button',
                      text: 'WRITE A REVIEW',
                      url: 'https://g.page/review',
                      buttonColor: '#4285f4',
                      style: { textAlign: 'center', padding: '15px' }
                    }
                  ]
                },
                { 
                  components: [
                    {
                      type: 'button',
                      text: 'LIKE US ON FACEBOOK',
                      url: 'https://facebook.com',
                      buttonColor: '#1877f2',
                      style: { textAlign: 'center', padding: '15px' }
                    }
                  ]
                }
              ],
              style: { padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>Thank you once again for choosing <strong>[account]</strong>. Wishing you joyful and safe journeys ahead.</p><p><strong>The [account] Team</strong></p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            }
          ]
        },
        {
          id: 'welcome-personal',
          name: 'Personal Welcome',
          description: 'Personalized with customer details',
          components: [
            {
              type: 'text',
              content: '<p>Hi <strong>{Buyers Name:1}</strong>,</p><p>Congratulations on your new <strong>{Buyers Vehicle:4}</strong>! We\'re delighted to welcome you to our Honda community.</p>',
              style: { color: '#374151', fontSize: 16, fontFamily: 'Arial, sans-serif', textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>Our priority is to ensure you and your vehicle receive the best care possible. If you have any questions or need support, please don\'t hesitate to contact us. We\'re here for you at every step!</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>We invite you to stay connected with us through our social media accounts. We value building meaningful, long-term relationships with our customers and encourage you to join us online.</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>If you\'ve had a wonderful experience with us, we\'d appreciate it if you could share your thoughts by leaving a review on Google or Facebook. Your feedback is invaluable to us.</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            },
            {
              type: 'columns',
              columnCount: 2,
              gap: 4,
              alignment: 'center',
              columns: [
                { 
                  components: [
                    {
                      type: 'button',
                      text: 'WRITE A REVIEW',
                      url: 'https://g.page/review',
                      buttonColor: '#4285f4',
                      style: { textAlign: 'center', padding: '15px' }
                    }
                  ]
                },
                { 
                  components: [
                    {
                      type: 'button',
                      text: 'LIKE US ON FACEBOOK',
                      url: 'https://facebook.com',
                      buttonColor: '#1877f2',
                      style: { textAlign: 'center', padding: '15px' }
                    }
                  ]
                }
              ],
              style: { padding: '25px' }
            },
            {
              type: 'text',
              content: '<p>Thank you once again for choosing <strong>[account]</strong>. Wishing you joyful and safe journeys ahead.</p><p><strong>The [account] Team</strong></p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
            }
          ]
        },
        {
          id: 'welcome-simple',
          name: 'Simple & Clean',
          description: 'Minimal welcome message',
          components: [
            {
              type: 'text',
              content: '<h2>Welcome aboard! 🎉</h2><p>Thank you for choosing our dealership. We\'re excited to help you find your perfect vehicle.</p>',
              style: { color: '#374151', fontSize: 16, textAlign: 'center', padding: '24px' }
            },
            {
              type: 'divider',
              style: { padding: '12px 0' }
            },
            {
              type: 'text',
              content: '<p>Ready to explore? Browse our range or get in touch with our team.</p>',
              style: { color: '#6b7280', fontSize: 14, textAlign: 'center', padding: '16px' }
            },
            {
              type: 'button',
              text: 'Get Started',
              url: 'https://example.com',
              buttonColor: '#059669',
              style: { textAlign: 'center', padding: '16px' }
            }
          ]
        },
        {
          id: 'welcome-onboarding',
          name: 'Onboarding Steps',
          description: 'Guide customers through next steps',
          components: [
            {
              type: 'text',
              content: '<h2>Welcome! Here\'s how to get started:</h2>',
              style: { color: '#1f2937', fontSize: 20, fontWeight: 'bold', textAlign: 'center', padding: '20px 20px 10px' }
            },
            {
              type: 'columns',
              columnCount: 3,
              gap: 4,
              alignment: 'center',
              columns: [
                { 
                  components: [
                    {
                      type: 'text',
                      content: '<div style="background: #dbeafe; border-radius: 50%; width: 60px; height: 60px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; font-size: 24px;">1</div><h4>Explore</h4><p>Browse our vehicle range and find what suits you best.</p>',
                      style: { color: '#374151', fontSize: 14, textAlign: 'center', padding: '16px' }
            }
          ]
        },
        {
          components: [
            {
              type: 'text',
                      content: '<div style="background: #dcfce7; border-radius: 50%; width: 60px; height: 60px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; font-size: 24px;">2</div><h4>Test Drive</h4><p>Experience the vehicle firsthand with a personalized test drive.</p>',
                      style: { color: '#374151', fontSize: 14, textAlign: 'center', padding: '16px' }
            }
          ]
        },
        {
          components: [
            {
              type: 'text',
                      content: '<div style="background: #fef3c7; border-radius: 50%; width: 60px; height: 60px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; font-size: 24px;">3</div><h4>Drive Away</h4><p>Complete your purchase and drive away in your new vehicle!</p>',
                      style: { color: '#374151', fontSize: 14, textAlign: 'center', padding: '16px' }
                    }
                  ]
                }
              ],
              style: { backgroundColor: 'transparent', padding: '20px' }
            },
            {
              type: 'button',
              text: 'Start Your Journey',
              url: 'https://example.com/new-vehicles',
              buttonColor: '#dc2626',
              buttonStyle: 'filled',
              style: { textAlign: 'center', padding: '24px' }
            }
          ]
        },
        {
          id: 'volvo-replica',
          name: 'Welcome Email Template',
          description: 'Professional welcome email with complete layout',
          components: [
            {
              type: 'image',
              src: '',
              alt: 'Hands exchanging car keys',
              style: { textAlign: 'center', padding: '0', width: '100%', height: 'auto' }
            },
            {
              type: 'text',
              content: '<p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 20px;">Hi <strong>{Buyers Name:1}</strong>,</p><p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 20px;">Congratulations on your new <strong>{Buyers Vehicle:4}</strong>! We\'re delighted to welcome you to our Honda community.</p>',
              style: { color: '#333333', fontSize: 16, fontFamily: 'Arial, sans-serif', textAlign: 'left', padding: '30px 40px 20px 40px' }
            },
            {
              type: 'text',
              content: '<p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 20px;">Our priority is to ensure you and your vehicle receive the best care possible. If you have any questions or need support, please don\'t hesitate to contact us. We\'re here for you at every step!</p><p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 20px;">We invite you to stay connected with us through our social media accounts. We value building meaningful, long-term relationships with our customers and encourage you to join us online.</p><p style="font-size: 16px; line-height: 1.6; color: #333;">If you\'ve had a wonderful experience with us, we\'d appreciate it if you could share your thoughts by leaving a review on Google or Facebook. Your feedback is invaluable to us.</p>',
              style: { color: '#333333', fontSize: 16, textAlign: 'left', padding: '0 40px 30px 40px' }
            },
            {
              type: 'text',
              content: '<p style="font-size: 16px; line-height: 1.6; color: #333; margin-bottom: 30px;">Thank you once again for choosing <strong>[account]</strong>. Wishing you joyful and safe journeys ahead.</p><p style="font-size: 16px; font-weight: bold; color: #333;">The <strong>[account]</strong> Team</p>',
              style: { color: '#333333', fontSize: 16, textAlign: 'left', padding: '0 40px 40px 40px' }
            },
            {
              type: 'columns',
              columnCount: 3,
              gap: 0,
              alignment: 'stretch',
              columns: [
                { 
                  components: [
                    {
                      type: 'image',
                      src: '',
                      alt: 'Professional woman in office setting',
                      style: { textAlign: 'center', padding: '0', width: '100%', height: '150px', objectFit: 'cover' }
            }
          ]
        },
        {
                  components: [
                    {
                      type: 'image',
                      src: '',
                      alt: 'Luxury car interior details',
                      style: { textAlign: 'center', padding: '0', width: '100%', height: '150px', objectFit: 'cover' }
                    }
                  ]
                },
                { 
                  components: [
                    {
                      type: 'image',
                      src: '',
                      alt: 'Person using car technology',
                      style: { textAlign: 'center', padding: '0', width: '100%', height: '150px', objectFit: 'cover' }
                    }
                  ]
                }
              ],
              style: { padding: '0', backgroundColor: 'transparent' }
            },
            {
              type: 'columns',
              columnCount: 2,
              gap: 0,
              alignment: 'stretch',
              columns: [
                { 
          components: [
            {
              type: 'text',
                      content: '<h3 style="font-size: 18px; font-weight: bold; margin-bottom: 15px; color: #1a1a1a; text-align: center;">Seek Your Accessories</h3><p style="font-size: 14px; color: #666; text-align: center; margin-bottom: 20px;">ACCESSORIES ENQUIRY</p>',
                      style: { color: '#333333', fontSize: 16, textAlign: 'center', padding: '40px 20px 10px 20px', backgroundColor: '#f8f9fa' }
                    },
                    {
                      type: 'button',
                      text: 'ACCESSORIES ENQUIRY',
                      url: 'https://example.com/accessories',
                      buttonColor: '#6c757d',
                      style: { textAlign: 'center', padding: '0 20px 40px 20px', backgroundColor: '#f8f9fa' }
                    }
                  ]
                },
                { 
                  components: [
                    {
                      type: 'text',
                      content: '<h3 style="font-size: 18px; font-weight: bold; margin-bottom: 15px; color: #1a1a1a; text-align: center;">Book Your Service</h3><p style="font-size: 14px; color: #666; text-align: center; margin-bottom: 20px;">BOOK YOUR SERVICE</p>',
                      style: { color: '#333333', fontSize: 16, textAlign: 'center', padding: '40px 20px 10px 20px', backgroundColor: '#f8f9fa' }
                    },
                    {
                      type: 'button',
                      text: 'BOOK YOUR SERVICE',
                      url: 'https://example.com/service',
                      buttonColor: '#6c757d',
                      style: { textAlign: 'center', padding: '0 20px 40px 20px', backgroundColor: '#f8f9fa' }
                    }
                  ]
                }
              ],
              style: { padding: '0', backgroundColor: '#f8f9fa' }
            },
            {
              type: 'text',
              content: '<h2 style="font-size: 22px; font-weight: bold; margin-bottom: 20px; color: #1a1a1a; text-align: center;">Earn $300* Per Referral</h2><p style="font-size: 16px; line-height: 1.6; color: #333; text-align: center;">As a valued member of our dealership, we invite you to join our referral program. You can earn $300 for every referral that makes a purchase from us.</p>',
              style: { color: '#333333', fontSize: 16, textAlign: 'center', padding: '50px 40px 30px 40px', backgroundColor: '#ffffff' }
            },
            {
              type: 'button',
              text: 'FIND OUT MORE',
              url: 'https://example.com/referral',
              buttonColor: '#007bff',
              style: { textAlign: 'center', padding: '0 40px 50px 40px', backgroundColor: '#ffffff' }
            },
            {
              type: 'image',
              src: '',
              alt: 'Person using car dashboard technology',
              style: { textAlign: 'center', padding: '0', width: '100%', height: '200px', objectFit: 'cover' }
            },
            {
              type: 'text',
              content: '<h3 style="font-size: 20px; font-weight: bold; margin-bottom: 30px; color: #1a1a1a; text-align: center;">Tell us what you think</h3>',
              style: { color: '#333333', fontSize: 20, textAlign: 'center', padding: '40px 40px 20px 40px' }
            },
            {
              type: 'columns',
              columnCount: 2,
              gap: 4,
              alignment: 'center',
              columns: [
                { 
                  components: [
                    {
                      type: 'button',
                      text: 'WRITE A REVIEW',
                      url: 'https://g.page/ferntree-gully-isuzu/review',
                      buttonColor: '#4285f4',
                      style: { textAlign: 'center', padding: '15px' }
                    }
                  ]
                },
                { 
                  components: [
                    {
                      type: 'button',
                      text: 'LIKE US ON FACEBOOK',
                      url: 'https://facebook.com/ferntreeisuzu',
                      buttonColor: '#1877f2',
                      style: { textAlign: 'center', padding: '15px' }
                    }
                  ]
                }
              ],
              style: { padding: '20px 40px 40px 40px' }
            },

          ]
        }
      ],
      
      // Column editing context
      editingColumnIndex: -1,
      editingColumnComponentIndex: -1,
      
      // Column component selection
      selectedColumnComponent: null, // { parentIndex, columnIndex, componentIndex }
      
      // Drag and drop state
      draggedComponent: null,
      
      // Social modal state
      showSocialsModal: false
    };
  },
  computed: {
    selectedComponent() {
      // If a column component is selected, return it
      if (this.selectedColumnComponent) {
        const { parentIndex, columnIndex, componentIndex } = this.selectedColumnComponent;
        return this.emailComponents[parentIndex].columns[columnIndex].components[componentIndex];
      }
      
      // Otherwise return the main component
      return this.selectedComponentIndex >= 0 ? this.emailComponents[this.selectedComponentIndex] : null;
    }
  },
  methods: {
    // Account data integration methods
    generateLogoText(accountName) {
      // Generate logo text from account name
      if (!accountName || typeof accountName !== 'string') {
        return 'AD'; // Default fallback for invalid input
      }
      
      const words = accountName.split(' ');
      if (words.length >= 2) {
        // Take first letter of first two words
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      } else if (words.length === 1) {
        // Take first two letters of single word
        return words[0].substring(0, 2).toUpperCase();
      }
      return 'AD'; // Default fallback
    },
    
    updateTemplateLibraryWithAccountData(account) {
      // Always update email settings with account-specific data when account data is available
      if (!account) {
        console.warn('updateTemplateLibraryWithAccountData called with invalid account data');
        return;
      }
      const accountName = account.name || 'Our Dealership';
      const logoText = this.generateLogoText(accountName);
      
      // Update email settings with account-specific data
      this.emailSettings = {
        ...this.emailSettings,
        subject: `Welcome to ${accountName}! Your Journey Starts Here`,
        headerTitle: `Welcome to ${accountName}!`,
        footerText: account.address ? 
          `${accountName} | ${account.address}` : 
          `${accountName} | Your trusted automotive partner`,
        logoText: logoText,
        headerColor: account.primaryColor || '#1e40af'
      };
      
      // If initialSettings were provided, merge them over the account defaults
      if (this.initialSettings) {
        this.emailSettings = { ...this.emailSettings, ...this.initialSettings };
      }
      
      // Update template library with account-specific templates
      const accountTemplate = {
        id: 'account-specific',
        name: `${accountName} Welcome`,
        description: `Customized welcome template for ${accountName}`,
        components: [
          {
            type: 'text',
            content: `<div style="text-align: center; padding: 60px 40px; background: linear-gradient(135deg, ${account.primaryColor || '#1e40af'} 0%, ${account.secondaryColor || '#3b82f6'} 100%); color: white; margin-bottom: 0;"><div style="background: rgba(255,255,255,0.1); padding: 20px; border-radius: 12px; display: inline-block; margin-bottom: 20px;"><div style="background: white; color: ${account.primaryColor || '#1e40af'}; width: 60px; height: 60px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: bold; margin: 0 auto;">${logoText}</div></div><h1 style="font-size: 32px; font-weight: bold; margin: 0 0 10px 0; text-shadow: 0 2px 4px rgba(0,0,0,0.1);">Welcome!</h1></div>`,
            style: {
              backgroundColor: 'transparent',
              padding: '0',
              margin: '0'
            }
          },
          {
            type: 'text',
            content: '<p>Hi <strong>{Buyers Name:1}</strong>,</p><p>Congratulations on your new <strong>{Buyers Vehicle:4}</strong>! We\'re delighted to welcome you to our Honda community.</p>',
            style: { color: '#374151', fontSize: 16, fontFamily: 'Arial, sans-serif', textAlign: 'left', padding: '25px' }
          },
          {
            type: 'text',
            content: '<p>Our priority is to ensure you and your vehicle receive the best care possible. If you have any questions or need support, please don\'t hesitate to contact us. We\'re here for you at every step!</p>',
            style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
          },
          {
            type: 'text',
            content: '<p>We invite you to stay connected with us through our social media accounts. We value building meaningful, long-term relationships with our customers and encourage you to join us online.</p>',
            style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
          },
          {
            type: 'text',
            content: '<p>If you\'ve had a wonderful experience with us, we\'d appreciate it if you could share your thoughts by leaving a review on Google or Facebook. Your feedback is invaluable to us.</p>',
            style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
          },
          {
            type: 'columns',
            columnCount: 2,
            gap: 4,
            alignment: 'center',
            columns: [
              { 
                components: [
                  {
                    type: 'button',
                    text: 'WRITE A REVIEW',
                    url: 'https://g.page/review',
                    buttonColor: '#4285f4',
                    style: { textAlign: 'center', padding: '15px' }
                  }
                ]
              },
              { 
                components: [
                  {
                    type: 'button',
                    text: 'LIKE US ON FACEBOOK',
                    url: 'https://facebook.com',
                    buttonColor: '#1877f2',
                    style: { textAlign: 'center', padding: '15px' }
                  }
                ]
              }
            ],
            style: { padding: '25px' }
          },
          {
            type: 'text',
            content: `<p>Thank you once again for choosing <strong>${accountName}</strong>. Wishing you joyful and safe journeys ahead.</p><p><strong>The ${accountName} Team</strong></p>`,
            style: { color: '#374151', fontSize: 16, textAlign: 'left', padding: '25px' }
          }
        ]
      };
      
      // Add or replace account-specific template in the library
      const existingIndex = this.templateLibrary.findIndex(t => t.id === 'account-specific');
      if (existingIndex !== -1) {
        // Replace existing account template
        this.templateLibrary.splice(existingIndex, 1, accountTemplate);
      } else {
        // Add new account template to the beginning
        this.templateLibrary.unshift(accountTemplate);
      }
    },

    
    // Integration mode methods
    loadTemplateData(template) {
      if (template.builderData && template.builderData.emailComponents) {
        this.emailComponents = JSON.parse(JSON.stringify(template.builderData.emailComponents));
      } else {
        this.emailComponents = [];
      }
      
      if (template.builderData && template.builderData.emailSettings) {
        this.emailSettings = { ...this.emailSettings, ...template.builderData.emailSettings };
      }
      

      
      if (template.subject) {
        this.emailSettings.subject = template.subject;
      }
      
      this.selectedComponentIndex = -1;
      this.addToHistory();
    },
    
    emitTemplateUpdate() {
      if (this.integrationMode) {
        const builderData = {
          emailComponents: JSON.parse(JSON.stringify(this.emailComponents)),
          emailSettings: { ...this.emailSettings }
        };
        
        const htmlBody = this.generateHtml();
        
        this.$emit('template-updated', {
          builderData,
          htmlBody,
          subject: this.emailSettings.subject
        });
      }
    },
    
    // Template management
    loadTemplate(template) {
      this.selectedTemplate = template;
      this.emailComponents = JSON.parse(JSON.stringify(template.components));
      this.selectedComponentIndex = -1;
      this.addToHistory();
      
      this.showNotification({
        type: 'success',
        title: 'Template Loaded',
        message: `${template.name} template loaded successfully!`
      });
    },
    
    // Preview functionality
    showPreview() {
      const html = this.generateHtml();
      const previewWindow = window.open('', '_blank');
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Email Preview</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; background-color: #f5f5f5; }
            .email-container { max-width: 600px; margin: 0 auto; background: white; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
          </style>
        </head>
        <body>
          <div class="email-container">
            ${html}
          </div>
        </body>
        </html>
      `);
      previewWindow.document.close();
    },
    
    // Component selection
    selectComponent(index) {
      this.selectedComponentIndex = index;
      this.selectedColumnComponent = null; // Clear column component selection
      this.editingIndex = -1;
      
      // Update padding controls based on selected component
      if (this.selectedComponent && this.selectedComponent.style) {
        const padding = this.selectedComponent.style.padding || '16px';
        const paddingValue = parseInt(padding.replace('px', ''));
        this.paddingTop = paddingValue;
        this.paddingLeft = paddingValue;
      }
      
      // Focus the rich text editor for text/heading components
      if (this.selectedComponent && 
          (this.selectedComponent.type === 'text' || this.selectedComponent.type === 'heading')) {
        this.$nextTick(() => {
          if (this.$refs.richTextEditor) {
            this.$refs.richTextEditor.focus();
          }
        });
      }
    },
    
    // Column component selection
    selectColumnComponent(parentIndex, columnIndex, componentIndex) {
      this.selectedComponentIndex = -1; // Clear main component selection
      this.selectedColumnComponent = { parentIndex, columnIndex, componentIndex };
      this.editingIndex = -1;
      
      // Update padding controls based on selected column component
      if (this.selectedComponent && this.selectedComponent.style) {
        const padding = this.selectedComponent.style.padding || '8px';
        const paddingValue = parseInt(padding.replace('px', ''));
        this.paddingTop = paddingValue;
        this.paddingLeft = paddingValue;
      }
      
      // Focus the rich text editor for text/heading components
      if (this.selectedComponent && 
          (this.selectedComponent.type === 'text' || this.selectedComponent.type === 'heading')) {
        this.$nextTick(() => {
          if (this.$refs.richTextEditor) {
            this.$refs.richTextEditor.focus();
          }
        });
      }
    },
    
    isColumnComponentSelected(parentIndex, columnIndex, componentIndex) {
      if (!this.selectedColumnComponent) return false;
      return this.selectedColumnComponent.parentIndex === parentIndex &&
             this.selectedColumnComponent.columnIndex === columnIndex &&
             this.selectedColumnComponent.componentIndex === componentIndex;
    },
    
    // Block type names for properties panel
    getBlockTypeName(type) {
      const names = {
        text: 'TEXT BLOCK',
        heading: 'HEADING BLOCK',
        button: 'BUTTON BLOCK',
        image: 'IMAGE BLOCK',
        spacer: 'SPACER BLOCK',
        divider: 'DIVIDER BLOCK',
        columns: 'COLUMNS BLOCK',
        hero: 'HERO BLOCK'
      };
      return names[type] || 'BLOCK';
    },
    
    // Component styling helpers
    setFontWeight(weight) {
      if (this.selectedComponent) {
        this.selectedComponent.style.fontWeight = weight;
        this.updateComponentStyle();
      }
    },
    
    setAlignment(align) {
      if (this.selectedComponent) {
        this.selectedComponent.style.textAlign = align;
        this.updateComponentStyle();
      }
    },
    
    setButtonStyle(style) {
      if (this.selectedComponent) {
        this.selectedComponent.buttonStyle = style;
        this.updateComponentContent();
      }
    },

    setColumnCount(count) {
      if (this.selectedComponent) {
        this.selectedComponent.columnCount = count;
        this.updateColumns();
        this.updateComponentContent();
      }
    },

    updateColumns() {
      const component = this.selectedComponent;
      const newCount = component.columnCount;
      const currentCount = component.columns.length;
      
      if (newCount > currentCount) {
        // Add columns
        for (let i = currentCount; i < newCount; i++) {
          component.columns.push({ components: [], width: 'auto' });
        }
      } else if (newCount < currentCount) {
        // Remove columns
        component.columns = component.columns.slice(0, newCount);
      }
      
      // Ensure Vue reactivity
      this.$set(this.selectedComponent, 'columns', component.columns);
    },

    setColumnAlignment(align) {
      if (this.selectedComponent) {
        this.selectedComponent.alignment = align;
        this.updateComponentContent();
      }
    },
    
    updatePadding() {
      if (this.selectedComponent) {
        this.selectedComponent.style.padding = `${this.paddingTop}px ${this.paddingLeft}px`;
        this.updateComponentStyle();
      }
    },
    
    updateComponentContent() {
      // If this is a column component, trigger Vue reactivity
      if (this.selectedColumnComponent) {
        const { parentIndex, columnIndex } = this.selectedColumnComponent;
        const targetColumn = this.emailComponents[parentIndex].columns[columnIndex];
        this.$set(this.emailComponents[parentIndex].columns, columnIndex, { ...targetColumn });
      }
      this.addToHistory();
    },
    
    updateComponentStyle() {
      // If this is a column component, trigger Vue reactivity
      if (this.selectedColumnComponent) {
        const { parentIndex, columnIndex } = this.selectedColumnComponent;
        const targetColumn = this.emailComponents[parentIndex].columns[columnIndex];
        this.$set(this.emailComponents[parentIndex].columns, columnIndex, { ...targetColumn });
      }
      this.addToHistory();
    },
    
    // Rich text formatting
    applyTextFormat(command, value = null) {
      // Focus the editor first
      if (this.$refs.richTextEditor) {
        this.$refs.richTextEditor.focus();
        
        // Apply the formatting command
        document.execCommand(command, false, value);
        
        // Update the component content
        this.updateRichTextContent();
      }
    },

    applyFontSize(size) {
      if (this.$refs.richTextEditor) {
        this.$refs.richTextEditor.focus();
        document.execCommand('fontSize', false, '7'); // fontSize uses 1-7 scale
        
        // Get the selection and apply custom font size
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const span = document.createElement('span');
          span.style.fontSize = size + 'px';
          
          try {
            range.surroundContents(span);
          } catch (e) {
            // If surroundContents fails, extract and wrap
            span.appendChild(range.extractContents());
            range.insertNode(span);
          }
        }
        
        this.updateRichTextContent();
      }
    },

    applyTextColor(color) {
      if (this.$refs.richTextEditor) {
        this.$refs.richTextEditor.focus();
        document.execCommand('foreColor', false, color);
        this.updateRichTextContent();
      }
    },

    updateRichTextContent() {
      if (this.$refs.richTextEditor && this.selectedComponent) {
        this.selectedComponent.content = this.$refs.richTextEditor.innerHTML;
        this.addToHistory();
        this.updateToolbarState();
        
        // If this is a column component, trigger Vue reactivity
        if (this.selectedColumnComponent) {
          const { parentIndex, columnIndex } = this.selectedColumnComponent;
          const targetColumn = this.emailComponents[parentIndex].columns[columnIndex];
          this.$set(this.emailComponents[parentIndex].columns, columnIndex, { ...targetColumn });
        }
      }
    },

    updateToolbarState() {
      // Update toolbar button states based on current selection
      this.$nextTick(() => {
        if (this.$refs.richTextEditor) {
          // This could be extended to highlight active formatting buttons
          // For now, just ensure the content is synced
        }
      });
    },

    updateCanvasSettings() {
      // Add canvas changes to history for undo/redo
      this.addToHistory();
      
      // Emit template update for integration mode
      this.emitTemplateUpdate();
    },

    insertLink() {
      const url = prompt('Enter URL:');
      if (url && this.$refs.richTextEditor) {
        this.$refs.richTextEditor.focus();
        document.execCommand('createLink', false, url);
        this.updateRichTextContent();
      }
    },

    clearFormatting() {
      if (this.$refs.richTextEditor) {
        this.$refs.richTextEditor.focus();
        document.execCommand('removeFormat', false, null);
        this.updateRichTextContent();
      }
    },

    handleKeyDown(event) {
      // Handle keyboard shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key.toLowerCase()) {
          case 'b':
            event.preventDefault();
            this.applyTextFormat('bold');
            break;
          case 'i':
            event.preventDefault();
            this.applyTextFormat('italic');
            break;
          case 'u':
            event.preventDefault();
            this.applyTextFormat('underline');
            break;
          case 'k':
            event.preventDefault();
            this.insertLink();
            break;
          case 'e':
            event.preventDefault();
            this.applyTextFormat('justifyCenter');
            break;
          case 'l':
            event.preventDefault();
            this.applyTextFormat('justifyLeft');
            break;
          case 'r':
            event.preventDefault();
            this.applyTextFormat('justifyRight');
            break;
          case 'd':
            event.preventDefault();
            // Duplicate selected component
            if (this.selectedComponentIndex >= 0 && this.selectedComponentIndex < this.emailComponents.length) {
              this.duplicateComponent(this.selectedComponentIndex);
            }
            break;
        }
      }
    },
    
    updateTextContent(index, event) {
      this.emailComponents[index].content = event.target.innerHTML;
      this.addToHistory();
    },
    
    // Component styling
    getComponentStyle(component) {
      const baseStyle = component.style || {};
      return {
        backgroundColor: baseStyle.backgroundColor || 'transparent',
        color: baseStyle.color || '#374151',
        padding: baseStyle.padding || '16px',
        textAlign: baseStyle.textAlign || 'left',
        fontSize: (baseStyle.fontSize || 16) + 'px',
        fontFamily: baseStyle.fontFamily || 'Arial, sans-serif',
        fontWeight: baseStyle.fontWeight || 'normal'
      };
    },
    
    getButtonStyle(component) {
      const isOutline = component.buttonStyle === 'outline';
      const buttonColor = component.buttonColor || '#3b82f6';
      
      return {
        backgroundColor: isOutline ? 'transparent' : buttonColor,
        color: isOutline ? buttonColor : '#ffffff',
        border: isOutline ? `2px solid ${buttonColor}` : 'none',
        textDecoration: 'none',
        display: 'inline-block',
        padding: '12px 24px',
        borderRadius: '6px',
        fontWeight: '500'
      };
    },

    getColumnComponentStyle(component) {
      const baseStyle = component.style || {};
      return {
        backgroundColor: baseStyle.backgroundColor || 'transparent',
        color: baseStyle.color || '#374151',
        padding: baseStyle.padding || '16px',
        textAlign: baseStyle.textAlign || 'left',
        fontSize: (baseStyle.fontSize || 16) + 'px',
        fontFamily: baseStyle.fontFamily || 'Arial, sans-serif',
        fontWeight: baseStyle.fontWeight || 'normal'
      };
    },
    
    // History management
    addToHistory() {
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(JSON.parse(JSON.stringify(this.emailComponents)));
      this.historyIndex = this.history.length - 1;
      
      if (this.history.length > 50) {
        this.history.shift();
        this.historyIndex--;
      }
      
      // Emit template update for integration mode
      this.emitTemplateUpdate();
    },
    
    undo() {
      if (this.historyIndex > 0) {
        this.historyIndex--;
        this.emailComponents = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
        this.selectedComponentIndex = -1;
      }
    },
    
    redo() {
      if (this.historyIndex < this.history.length - 1) {
        this.historyIndex++;
        this.emailComponents = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
        this.selectedComponentIndex = -1;
      }
    },
    
    // Component management
    moveComponent(index, direction) {
      const newIndex = index + direction;
      if (newIndex >= 0 && newIndex < this.emailComponents.length) {
        const component = this.emailComponents[index];
        this.emailComponents.splice(index, 1);
        this.emailComponents.splice(newIndex, 0, component);
        this.selectedComponentIndex = newIndex;
        this.addToHistory();
      }
    },
    
    deleteComponent(index) {
      this.emailComponents.splice(index, 1);
      this.selectedComponentIndex = -1;
      this.addToHistory();
    },
    
    editComponent(index) {
      this.editingIndex = index;
      this.selectedComponentIndex = index;
      
      this.$nextTick(() => {
        if (this.$refs.textEditor) {
          this.$refs.textEditor.focus();
        }
      });
    },



    deleteColumnComponent(parentIndex, columnIndex, componentIndex) {
      // Clear selection if this component is selected
      if (this.isColumnComponentSelected(parentIndex, columnIndex, componentIndex)) {
        this.selectedColumnComponent = null;
        this.selectedComponentIndex = -1;
      }
      
      this.emailComponents[parentIndex].columns[columnIndex].components.splice(componentIndex, 1);
      
      // Trigger Vue reactivity for the column
      const targetColumn = this.emailComponents[parentIndex].columns[columnIndex];
      this.$set(this.emailComponents[parentIndex].columns, columnIndex, { ...targetColumn });
      
      this.editingIndex = -1; // Reset editing index
      this.addToHistory();
    },
    
    duplicateComponent(index) {
      // Deep clone the component to avoid reference issues
      const originalComponent = this.emailComponents[index];
      const duplicatedComponent = JSON.parse(JSON.stringify(originalComponent));
      
      // Generate new unique ID for the duplicated component
      duplicatedComponent.id = Date.now() + Math.random();
      
      // If it's a columns component, generate new IDs for nested components too
      if (duplicatedComponent.type === 'columns' && duplicatedComponent.columns) {
        duplicatedComponent.columns.forEach(column => {
          if (column.components) {
            column.components.forEach(comp => {
              comp.id = Date.now() + Math.random();
            });
          }
        });
      }
      
      // Insert the duplicated component right after the original
      this.emailComponents.splice(index + 1, 0, duplicatedComponent);
      
      // Select the duplicated component
      this.selectedComponentIndex = index + 1;
      this.addToHistory();
      
      // Show success notification
      this.showNotification({
        type: 'success',
        title: 'Component Duplicated',
        message: `${this.getBlockTypeName(originalComponent.type)} component duplicated successfully`
      });
    },
    
    duplicateColumnComponent(parentIndex, columnIndex, componentIndex) {
      // Deep clone the column component
      const originalComponent = this.emailComponents[parentIndex].columns[columnIndex].components[componentIndex];
      const duplicatedComponent = JSON.parse(JSON.stringify(originalComponent));
      
      // Generate new unique ID
      duplicatedComponent.id = Date.now() + Math.random();
      
      // Insert the duplicated component right after the original
      this.emailComponents[parentIndex].columns[columnIndex].components.splice(componentIndex + 1, 0, duplicatedComponent);
      
      // Trigger Vue reactivity for the column
      const targetColumn = this.emailComponents[parentIndex].columns[columnIndex];
      this.$set(this.emailComponents[parentIndex].columns, columnIndex, { ...targetColumn });
      
      // Select the duplicated component
      this.selectedColumnComponent = { 
        parentIndex, 
        columnIndex, 
        componentIndex: componentIndex + 1 
      };
      this.selectedComponentIndex = -1;
      
      this.addToHistory();
      
      // Show success notification
      this.showNotification({
        type: 'success',
        title: 'Component Duplicated',
        message: `${this.getBlockTypeName(originalComponent.type)} component duplicated successfully`
      });
    },
    
    stopEditing() {
      this.editingIndex = -1;
      this.addToHistory();
    },
    
    // Drag and drop
    dragStart(componentType, event) {
      event.dataTransfer.setData('componentType', componentType);
    },
    
    startDragExistingComponent(sourceType, sourceData, event) {
      // Prevent bubbling to avoid interference
      event.stopPropagation();
      
      // Validate source data before starting drag
      if (sourceType === 'main') {
        const sourceIndex = sourceData;
        if (!this.emailComponents || sourceIndex >= this.emailComponents.length || sourceIndex < 0) {
          console.error('Invalid main component index for drag:', sourceIndex);
          event.preventDefault();
          return;
        }
      } else if (sourceType === 'column') {
        const { parentIndex, columnIndex, componentIndex } = sourceData;
        if (!this.emailComponents || 
            parentIndex >= this.emailComponents.length || 
            parentIndex < 0 ||
            !this.emailComponents[parentIndex] ||
            !this.emailComponents[parentIndex].columns ||
            columnIndex >= this.emailComponents[parentIndex].columns.length ||
            columnIndex < 0 ||
            !this.emailComponents[parentIndex].columns[columnIndex] ||
            !this.emailComponents[parentIndex].columns[columnIndex].components ||
            componentIndex >= this.emailComponents[parentIndex].columns[columnIndex].components.length ||
            componentIndex < 0) {
          console.error('Invalid column component location for drag:', sourceData);
          event.preventDefault();
          return;
        }
      }
      
      // Set drag data for existing component
      event.dataTransfer.setData('sourceType', sourceType);
      event.dataTransfer.setData('sourceData', JSON.stringify(sourceData));
      
      // Set drag effect
      event.dataTransfer.effectAllowed = 'move';
      
      // Add visual feedback to the drag handle
      event.target.style.opacity = '0.5';
      event.target.style.transform = 'scale(0.9)';
      
      // Add dragging class to body to prevent text selection
      document.body.classList.add('dragging');
      
      this.draggedComponent = { sourceType, sourceData };
    },
    
    endDragExistingComponent(event) {
      // Reset visual feedback
      event.target.style.opacity = '1';
      event.target.style.transform = 'scale(1)';
      
      // Remove dragging class from body
      document.body.classList.remove('dragging');
      
      this.draggedComponent = null;
      
      console.log('Ended dragging');
    },
    
    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      
      // Add visual feedback to drop zone
      if (event.target.classList.contains('column-drop-zone') || event.target.closest('.column-drop-zone')) {
        event.stopPropagation(); // Stop bubbling for column drag over
        const dropZone = event.target.classList.contains('column-drop-zone') ? event.target : event.target.closest('.column-drop-zone');
        dropZone.classList.add('drag-over');
      }
    },
    
    onDragEnter(event) {
      event.preventDefault();
      
      // Stop propagation if this is a column drop zone
      if (event.target.classList.contains('column-drop-zone') || event.target.closest('.column-drop-zone')) {
        event.stopPropagation();
      }
    },
    
    onDragLeave(event) {
      // Remove visual feedback when leaving drop zone
      if (event.target.classList.contains('column-drop-zone')) {
        event.stopPropagation(); // Stop bubbling for column drag leave
        event.target.classList.remove('drag-over');
      }
    },

    onColumnDrop(event, parentIndex, columnIndex) {
      event.preventDefault();
      event.stopPropagation(); // Prevent event from bubbling to main drop handler
      
      // Clean up visual feedback
      event.target.classList.remove('drag-over');
      
      // Check for new component from palette
      const componentType = event.dataTransfer.getData('componentType');
      if (componentType) {
        this.addComponentToColumn(componentType, parentIndex, columnIndex);
        return;
      }
      
      // Check for existing component being moved
      const sourceType = event.dataTransfer.getData('sourceType');
      const sourceData = event.dataTransfer.getData('sourceData');
      
      if (sourceType && sourceData) {
        const parsedSourceData = JSON.parse(sourceData);
        
        // Validate that target is a valid columns component before attempting move
        if (this.emailComponents[parentIndex] && 
            this.emailComponents[parentIndex].type === 'columns' && 
            this.emailComponents[parentIndex].columns &&
            this.emailComponents[parentIndex].columns[columnIndex]) {
          this.moveExistingComponentToColumn(sourceType, parsedSourceData, parentIndex, columnIndex);
        } else {
          console.error('Invalid drop target: not a valid column');
        }
      }
    },
    
    onDrop(event) {
      event.preventDefault();
      
      // Clean up visual feedback
      document.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'));
      
      // Check for new component from palette
      const componentType = event.dataTransfer.getData('componentType');
      if (componentType) {
        this.addComponent(componentType);
        return;
      }
      
      // Check for existing component being moved
      const sourceType = event.dataTransfer.getData('sourceType');
      const sourceData = event.dataTransfer.getData('sourceData');
      
      if (sourceType && sourceData) {
        const parsedSourceData = JSON.parse(sourceData);
        this.moveExistingComponentToMain(sourceType, parsedSourceData);
      }
    },
    
    addComponentToColumn(type, parentIndex, columnIndex) {
      const component = {
        type: type,
        id: Date.now(),
        style: {
          backgroundColor: 'transparent',
          color: '#374151',
          padding: '8px',
          textAlign: 'left',
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal'
        }
      };
      
      switch (type) {
        case 'text':
          component.content = 'Our priority is to ensure you and your vehicle receive the best care possible.';
          break;
        case 'heading':
          component.content = 'Welcome {Buyers Name:1}!';
          component.style.fontSize = 18;
          component.style.fontWeight = 'bold';
          break;
        case 'image':
          component.src = '';
          component.alt = 'Image';
          component.style.textAlign = 'center';
          break;
        case 'button':
          component.text = 'Learn More';
          component.url = this.accountData && this.accountData[0] && this.accountData[0].website ? this.accountData[0].website : 'https://example.com';
          component.buttonColor = '#1e40af';
          component.buttonStyle = 'filled';
          component.style.textAlign = 'center';
          break;
      }
      
      // Initialize components array if it doesn't exist
      if (!this.emailComponents[parentIndex].columns[columnIndex].components) {
        this.$set(this.emailComponents[parentIndex].columns[columnIndex], 'components', []);
      }
      
      // Add component to column
      const targetColumn = this.emailComponents[parentIndex].columns[columnIndex];
      targetColumn.components.push(component);
      
      // Trigger Vue reactivity for the column
      this.$set(this.emailComponents[parentIndex].columns, columnIndex, { ...targetColumn });
      
      this.selectedComponentIndex = parentIndex;
      this.addToHistory();
    },
    
    addComponent(type) {
      const component = {
        type: type,
        id: Date.now(),
        style: {
          backgroundColor: 'transparent',
          color: '#374151',
          padding: '16px',
          textAlign: 'left',
          fontSize: 16,
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal'
        }
      };
      
      switch (type) {
        case 'text':
          component.content = 'Hi <strong>{Buyers Name:1}</strong>, congratulations on your new <strong>{Buyers Vehicle:4}</strong>! We\'re delighted to welcome you to our Honda community.';
          break;
        case 'heading':
          component.content = 'Welcome to Our Community';
          component.style.fontSize = 24;
          component.style.fontWeight = 'bold';
          break;
        case 'image':
          component.src = '';
          component.alt = 'Image';
          component.style.textAlign = 'center';
          break;
        case 'button':
          component.text = 'VIEW OUR RANGE';
          component.url = this.accountData && this.accountData[0] && this.accountData[0].website ? `${this.accountData[0].website}/new-vehicles` : 'https://example.com/vehicles';
          component.buttonColor = '#1e40af';
          component.buttonStyle = 'filled';
          component.style.textAlign = 'center';
          break;
        case 'spacer':
          component.height = 40;
          break;
        case 'divider':
          component.style.padding = '20px 0';
          break;
        case 'columns':
          component.columnCount = 2;
          component.gap = 4;
          component.alignment = 'left';
          component.columns = [
            { components: [] },
            { components: [] }
          ];
          break;
        case 'hero':
          component.title = 'Welcome {Buyers Name:1}!';
          component.subtitle = 'Thank you for choosing us';
          component.description = 'Congratulations on your new {Buyers Vehicle:4}! We\'re delighted to welcome you to our Honda community and look forward to serving you.';
          component.buttonText = 'Connect With Us';
          component.buttonUrl = this.accountData && this.accountData[0] && this.accountData[0].website ? `${this.accountData[0].website}` : 'https://example.com';
          component.backgroundImage = '';
          component.backgroundGradient = 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)';
          component.backgroundColor = '#1e40af';
          component.height = 400;
          component.borderRadius = 12;
          component.textAlignment = 'left';
          component.contentPosition = 'center-left';
          component.titleColor = '#ffffff';
          component.titleSize = 36;
          component.subtitleColor = '#ffffff';
          component.subtitleSize = 24;
          component.descriptionColor = '#ffffff';
          component.descriptionSize = 16;
          component.buttonColor = '#1e40af';
          component.buttonBackgroundColor = '#ffffff';
          component.overlay = true;
          component.overlayColor = 'rgba(30, 64, 175, 0.1)';
          break;
        case 'social':
          this.showSocialsModal = true;
          return; // Don't add component yet, wait for modal
      }
      
      this.emailComponents.push(component);
      this.selectedComponentIndex = this.emailComponents.length - 1;
      this.addToHistory();
    },

    addSocialComponent(socialData) {
      const component = {
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        type: 'social',
        links: socialData.links,
        socialStyle: socialData.style,
        style: {
          backgroundColor: 'transparent',
          color: '#374151',
          padding: '16px',
          textAlign: 'center',
          fontSize: 16,
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal'
        }
      };

      this.emailComponents.push(component);
      this.selectedComponentIndex = this.emailComponents.length - 1;
      this.addToHistory();
    },

    getSocialButtonStyle(style) {
      if (!style) return {};
      
      const borderRadius = {
        circle: '50%',
        rounded: '8px',
        square: '0px'
      };
      
      return {
        width: (style.size || 40) + 'px',
        height: (style.size || 40) + 'px',
        borderRadius: borderRadius[style.style] || '50%',
        fontSize: Math.floor((style.size || 40) * 0.4) + 'px',
        lineHeight: (style.size || 40) + 'px'
      };
    },
    
    // Template actions
    saveTemplate() {
      const templateData = {
        name: this.emailSettings.subject || 'Untitled Template',
        subject: this.emailSettings.subject,
        builderData: {
          emailComponents: JSON.parse(JSON.stringify(this.emailComponents)),
          emailSettings: { ...this.emailSettings }
        },
        htmlBody: this.generateHtml(),
        accountSlug: this.accountSlug,
        accountData: this.accountData
      };
      
      // Emit save event for parent component
      this.$emit('template-saved', templateData);
      
      this.showNotification({
        type: 'success',
        title: 'Template Saved',
        message: 'Your email template has been saved successfully!'
      });
    },
    
    exportHtml() {
      const html = this.generateHtml();
      this.downloadHtml(html);
      
      // Emit export event for parent component
      this.$emit('export-html', {
        html: html,
        subject: this.emailSettings.subject,
        accountSlug: this.accountSlug
      });
      
      this.showNotification({
        type: 'success',
        title: 'HTML Exported',
        message: 'Email HTML has been exported and downloaded!'
      });
    },
    
    generateHtml() {
      return `<!doctype html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <title>${this.emailSettings.subject}</title>
    <!--[if !mso]><!-->
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <!--<![endif]-->
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style type="text/css">
        #outlook a { padding: 0; }
        body { margin: 0; padding: 0; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
        table, td { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
        img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
        p { display: block; margin: 0; }
  </style>
    <!--[if mso]> <noscript><xml><o:OfficeDocumentSettings><o:AllowPNG/><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
    <!--[if lte mso 11]><style type="text/css">.ogf{width:100% !important;}</style><![endif]-->
    <style type="text/css">
        @media only screen and (min-width:599px) {
            .pc100 { width: 100% !important; max-width: 100%; }
            .xc536 { width: 536px !important; max-width: 536px; }
            .xc568 { width: 568px !important; max-width: 568px; }
            .xc600 { width: 600px !important; max-width: 600px; }
        }
    </style>
    <style media="screen and (min-width:599px)">
        .moz-text-html .pc100 { width: 100% !important; max-width: 100%; }
        .moz-text-html .xc536 { width: 536px !important; max-width: 536px; }
        .moz-text-html .xc568 { width: 568px !important; max-width: 568px; }
        .moz-text-html .xc600 { width: 600px !important; max-width: 600px; }
    </style>
    <style type="text/css">
        @media only screen and (max-width:598px) {
            table.fwm { width: 100% !important; }
            td.fwm { width: auto !important; }
        }
        u+.emailify .gs { background: #000; mix-blend-mode: screen; display: inline-block; padding: 0; margin: 0; }
        u+.emailify .gd { background: #000; mix-blend-mode: difference; display: inline-block; padding: 0; margin: 0; }
        p { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        a[x-apple-data-detectors] { color: inherit !important; text-decoration: none !important; }
        u+.emailify a { color: inherit !important; text-decoration: none !important; }
        #MessageViewBody a { color: inherit !important; text-decoration: none !important; }
        @media only screen and (max-width:599px) {
            .emailify { height: 100% !important; margin: 0 !important; padding: 0 !important; width: 100% !important; }
            .fwm img { max-width: 100% !important; height: auto !important; }
            td.x { padding-left: 0 !important; padding-right: 0 !important; }
        }
    </style>
    <meta name="color-scheme" content="light dark">
    <meta name="supported-color-schemes" content="light dark">
    <!--[if gte mso 9]><style>a:link,span.MsoHyperlink{mso-style-priority:99;color:inherit;text-decoration:none;}a:visited,span.MsoHyperlinkFollowed{mso-style-priority:99;color:inherit;text-decoration:none;}</style><![endif]-->
</head>
<body lang="en" link="#DD0000" vlink="#DD0000" class="emailify" style="mso-line-height-rule:exactly;mso-hyphenate:none;word-spacing:normal;background-color:#f5f5f5;">
    <div class="bg" style="background-color:#f5f5f5;" lang="en">
        ${this.generateEmailHeader()}
        ${this.generateEmailBody()}
        ${this.generateEmailFooter()}
    </div>
</body>
</html>`;
    },

    generateEmailHeader() {
      if (!this.emailSettings.showHeader) return '';
      
      return `
        <!--[if mso | IE]><table align="center" border="0" cellpadding="0" cellspacing="0" class="r-outlook -outlook pr-16-outlook pl-16-outlook -outlook" role="presentation" style="width:600px;" width="600"><tr><td style="line-height:0;font-size:0;mso-line-height-rule:exactly;"><![endif]-->
        <div class="r pr-16 pl-16" style="background:${this.emailSettings.headerColor};background-color:${this.emailSettings.headerColor};margin:0px auto;max-width:600px;">
            <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background:${this.emailSettings.headerColor};background-color:${this.emailSettings.headerColor};width:100%;">
                <tbody>
                    <tr>
                        <td style="border:none;direction:ltr;font-size:0;padding:${this.emailSettings.containerPadding}px;text-align:center;">
                            <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><tr><td class="c-outlook -outlook -outlook" style="vertical-align:middle;width:536px;"><![endif]-->
                            <div class="xc536 ogf c" style="font-size:0;text-align:left;direction:ltr;display:inline-block;vertical-align:middle;width:100%;">
                                <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border:none;vertical-align:middle;" width="100%">
                                    <tbody>
                                        <tr>
                                            <td align="center" class="x" style="font-size:0;word-break:break-word;">
                                                <div style="text-align:center;">
                                                    <div style="width: 48px; height: 48px; background: white; border-radius: 50%; margin: 0 auto 12px; display: inline-flex; align-items: center; justify-content: center;">
        <span style="color: #1f2937; font-weight: bold; font-size: 18px;">${this.emailSettings.logoText}</span>
      </div>
                                                    <p style="Margin:0;text-align:center;mso-line-height-alt:24px;mso-ansi-font-size:18px;">
                                                        <span style="font-size:18px;font-family:Arial,sans-serif;font-weight:500;color:#ffffff;line-height:133%;mso-line-height-alt:24px;mso-ansi-font-size:18px;">${this.emailSettings.headerTitle}</span>
                                                    </p>
    </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <!--[if mso | IE]></td></tr></table><![endif]-->
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!--[if mso | IE]></td></tr></table><![endif]-->`;
    },

    generateEmailBody() {
      return `
        <!--[if mso | IE]><table align="center" border="0" cellpadding="0" cellspacing="0" class="r-outlook -outlook pr-16-outlook pl-16-outlook -outlook" role="presentation" style="width:600px;" width="600"><tr><td style="line-height:0;font-size:0;mso-line-height-rule:exactly;"><![endif]-->
        <div class="r pr-16 pl-16" style="background:${this.emailSettings.backgroundColor};background-color:${this.emailSettings.backgroundColor};margin:0px auto;max-width:600px;">
            <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background:${this.emailSettings.backgroundColor};background-color:${this.emailSettings.backgroundColor};width:100%;">
                <tbody>
                    <tr>
                        <td style="border:none;direction:ltr;font-size:0;padding:${this.emailSettings.containerPadding}px;text-align:left;">
                            <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><tr><td class="c-outlook -outlook -outlook" style="vertical-align:middle;width:536px;"><![endif]-->
                            <div class="xc536 ogf c" style="font-size:0;text-align:left;direction:ltr;display:inline-block;vertical-align:middle;width:100%;">
                                <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border:none;vertical-align:middle;" width="100%">
                                    <tbody>
                                        ${this.emailComponents.map(component => this.generateComponentHtml(component)).join('')}
                                    </tbody>
                                </table>
                            </div>
                            <!--[if mso | IE]></td></tr></table><![endif]-->
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!--[if mso | IE]></td></tr></table><![endif]-->`;
    },

    generateEmailFooter() {
      return `
        <!--[if mso | IE]><table align="center" border="0" cellpadding="0" cellspacing="0" class="r-outlook -outlook pr-16-outlook pl-16-outlook -outlook" role="presentation" style="width:600px;" width="600"><tr><td style="line-height:0;font-size:0;mso-line-height-rule:exactly;"><![endif]-->
        <div class="r pr-16 pl-16" style="background:${this.emailSettings.backgroundColor};background-color:${this.emailSettings.backgroundColor};margin:0px auto;max-width:600px;">
            <table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="background:${this.emailSettings.backgroundColor};background-color:${this.emailSettings.backgroundColor};width:100%;">
                <tbody>
                    <tr>
                        <td style="border:none;direction:ltr;font-size:0;padding:16px ${this.emailSettings.containerPadding}px;text-align:left;">
                            <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><tr><td class="c-outlook -outlook -outlook" style="vertical-align:middle;width:536px;"><![endif]-->
                            <div class="xc536 ogf c" style="font-size:0;text-align:left;direction:ltr;display:inline-block;vertical-align:middle;width:100%;">
                                <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border:none;vertical-align:middle;" width="100%">
                                    <tbody>
                                        <tr>
                                            <td align="center" class="x" style="font-size:0;word-break:break-word;border-top:1px solid #e5e7eb;padding-top:16px;">
                                                <div style="text-align:center;">
                                                    <p style="Margin:0;text-align:center;mso-line-height-alt:18px;mso-ansi-font-size:14px;">
                                                        <span style="font-size:13px;font-family:Arial,sans-serif;font-weight:400;color:#6b7280;line-height:140%;mso-line-height-alt:18px;mso-ansi-font-size:14px;">${this.emailSettings.footerText}</span>
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <!--[if mso | IE]></td></tr></table><![endif]-->
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!--[if mso | IE]></td></tr></table><![endif]-->`;
    },

    generateComponentHtml(component) {
        const style = this.getComponentStyle(component);
        
        switch (component.type) {
          case 'text': {
            return `
            <tr>
                <td align="left" class="x m" style="font-size:0;padding-bottom:16px;word-break:break-word;">
                    <div style="text-align:${style.textAlign || 'left'};">
                        <p style="Margin:0;text-align:${style.textAlign || 'left'};mso-line-height-alt:24px;mso-ansi-font-size:${style.fontSize}px;">
                            <span style="font-size:${style.fontSize}px;font-family:${style.fontFamily};font-weight:${style.fontWeight};color:${style.color};line-height:150%;mso-line-height-alt:24px;mso-ansi-font-size:${style.fontSize}px;">${component.content}</span>
                        </p>
                    </div>
                </td>
            </tr>`;
          }
            
          case 'heading': {
            return `
            <tr>
                <td align="left" class="x" style="font-size:0;padding-bottom:16px;word-break:break-word;">
                    <div style="text-align:${style.textAlign || 'left'};">
                        <p style="Margin:0;text-align:${style.textAlign || 'left'};mso-line-height-alt:${Math.round(style.fontSize * 1.2)}px;mso-ansi-font-size:${style.fontSize}px;">
                            <span style="font-size:${style.fontSize}px;font-family:${style.fontFamily};font-weight:bold;color:${style.color};line-height:113%;mso-line-height-alt:${Math.round(style.fontSize * 1.2)}px;mso-ansi-font-size:${style.fontSize}px;">${component.content}</span>
                        </p>
                    </div>
                </td>
            </tr>`;
          }
            
          case 'image': {
            return `
            <tr>
                <td align="${style.textAlign || 'center'}" class="i fw-1" style="font-size:0;padding-bottom:16px;word-break:break-word;">
                    <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse:collapse;border-spacing:0;" class="fwm">
                        <tbody>
                            <tr>
                                <td style="width:536px;" class="fwm">
                                    ${component.src ? `<img alt="${component.alt || ''}" src="${component.src}" style="border:0;display:block;outline:none;text-decoration:none;height:auto;width:100%;font-size:13px;" width="536" height="auto">` : `<div style="background:#f3f4f6;height:200px;display:flex;align-items:center;justify-content:center;color:#9ca3af;">Image Placeholder</div>`}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </td>
            </tr>`;
          }
            
          case 'button': {
            const buttonStyle = this.getButtonStyle(component);
            return `
            <tr>
                <td align="${style.textAlign || 'center'}" vertical-align="middle" class="b fw-1" style="font-size:0;padding-bottom:16px;word-break:break-word;">
                    <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse:separate;width:240px;line-height:100%;">
                        <tbody>
                            <tr>
                                <td align="center" bgcolor="${buttonStyle.backgroundColor}" role="presentation" style="border:${buttonStyle.border || 'none'};border-radius:6px;cursor:auto;mso-padding-alt:12px 24px;background:${buttonStyle.backgroundColor};" valign="middle">
                                    <a href="${component.url || '#'}" style="display:inline-block;width:192px;background:${buttonStyle.backgroundColor};color:${buttonStyle.color};font-family:Arial,sans-serif;font-size:16px;font-weight:500;line-height:100%;margin:0;text-decoration:none;text-transform:none;padding:12px 24px;mso-padding-alt:0;border-radius:6px;" target="_blank">
                                        <span style="font-size:16px;font-family:Arial,sans-serif;font-weight:500;color:${buttonStyle.color};line-height:100%;">${component.text}</span>
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </td>
            </tr>`;
          }
            
          case 'spacer': {
            return `
            <tr>
                <td class="s" style="font-size:0;padding:0;word-break:break-word;" aria-hidden="true">
                    <div style="height:${component.height || 40}px;line-height:${component.height || 40}px;">&#8202;</div>
                </td>
            </tr>`;
          }
            
          case 'divider': {
            return `
            <tr>
                <td align="center" class="x" style="font-size:0;padding:16px 0;word-break:break-word;">
                    <div style="height:1px;background-color:#e5e7eb;width:100%;"></div>
                </td>
            </tr>`;
          }
            
          case 'hero': {
            let heroBackgroundStyle = '';
            if (component.backgroundImage) {
              heroBackgroundStyle = `background-image: url('${component.backgroundImage}'); background-size: cover; background-position: center; background-repeat: no-repeat;`;
              if (component.overlay) {
                heroBackgroundStyle = `background-image: linear-gradient(${component.overlayColor || 'rgba(0,0,0,0.3)'}, ${component.overlayColor || 'rgba(0,0,0,0.3)'}), url('${component.backgroundImage}'); background-size: cover; background-position: center; background-repeat: no-repeat;`;
              }
            } else if (component.backgroundGradient) {
              heroBackgroundStyle = `background: ${component.backgroundGradient};`;
            } else {
              heroBackgroundStyle = `background-color: ${component.backgroundColor || '#1e40af'};`;
            }
          
            return `
            <tr>
                <td align="center" class="i fw-1" style="font-size:0;padding-bottom:16px;word-break:break-word;">
                    <table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse:collapse;border-spacing:0;${heroBackgroundStyle}border-radius:${component.borderRadius || 8}px;overflow:hidden;" class="fwm">
                        <tbody>
                            <tr>
                                <td style="width:536px;padding:32px;vertical-align:middle;min-height:${component.height || 300}px;" class="fwm">
                                    <div style="text-align:${component.textAlignment || 'left'};">
                                        ${component.title ? `<h2 style="color:${component.titleColor || '#ffffff'};font-size:${component.titleSize || 32}px;font-weight:bold;line-height:1.2;margin:0 0 16px 0;">${component.title}</h2>` : ''}
                                        ${component.subtitle ? `<p style="color:${component.subtitleColor || '#ffffff'};font-size:${component.subtitleSize || 20}px;font-weight:500;line-height:1.3;margin:0 0 16px 0;">${component.subtitle}</p>` : ''}
                                        ${component.description ? `<p style="color:${component.descriptionColor || '#ffffff'};font-size:${component.descriptionSize || 16}px;line-height:1.5;margin:0 0 24px 0;">${component.description}</p>` : ''}
                                        ${component.buttonText ? `<a href="${component.buttonUrl || '#'}" style="display:inline-block;background-color:${component.buttonBackgroundColor || '#ffffff'};color:${component.buttonColor || '#1e40af'};padding:12px 24px;border-radius:6px;text-decoration:none;font-size:16px;font-weight:500;" target="_blank">${component.buttonText}</a>` : ''}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </td>
            </tr>`;
          }
            
          case 'columns': {
            const columnWidth = Math.floor(536 / (component.columnCount || 2));
            const columnsHtml = component.columns.map((column, index) => {
              const columnContent = column.components ? column.components.map(colComp => {
                switch (colComp.type) {
                  case 'text':
                    return `<p style="margin:0 0 8px 0;font-size:14px;line-height:1.4;color:#374151;">${colComp.content}</p>`;
                  case 'heading':
                    return `<h3 style="margin:0 0 8px 0;font-size:16px;font-weight:bold;color:#374151;">${colComp.content}</h3>`;
                  case 'image':
                    return colComp.src ? `<img src="${colComp.src}" alt="${colComp.alt}" style="max-width:100%;height:auto;display:block;">` : '<div style="background:#f3f4f6;height:100px;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:12px;">Image</div>';
                  case 'button': {
                    const colButtonStyle = this.getButtonStyle(colComp);
                    return `<a href="${colComp.url}" style="display:inline-block;background-color:${colButtonStyle.backgroundColor};color:${colButtonStyle.color};padding:8px 16px;border-radius:4px;text-decoration:none;font-size:14px;font-weight:500;" target="_blank">${colComp.text}</a>`;
                  }
                  default:
                    return '';
                }
              }).join('') : '';
              
              return `
              <!--[if mso | IE]><td style="vertical-align:top;width:${columnWidth}px;"><![endif]-->
              <div style="font-size:0;text-align:left;direction:ltr;display:inline-block;vertical-align:top;width:${Math.floor(100 / (component.columnCount || 2))}%;">
                  <table border="0" cellpadding="0" cellspacing="0" role="presentation" width="100%">
                      <tbody>
                          <tr>
                              <td style="vertical-align:top;padding:16px;">
                                  ${columnContent}
                              </td>
                          </tr>
                      </tbody>
                  </table>
                  </div>
              <!--[if mso | IE]></td>${index < component.columns.length - 1 ? '' : '</tr>'}<![endif]-->`;
            }).join('');
            
            return `
            <tr>
                <td style="font-size:0;padding-bottom:16px;word-break:break-word;">
                    <!--[if mso | IE]><table role="presentation" border="0" cellpadding="0" cellspacing="0"><tr><![endif]-->
                    ${columnsHtml}
                    <!--[if mso | IE]></table><![endif]-->
                </td>
            </tr>`;
          }
            
          default:
            return '';
        }
    },
    
    downloadHtml(html) {
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'email-template.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    
    // Notification system
    showNotification({ type, title, message }) {
      this.notification = { show: true, type, title, message };
      setTimeout(() => this.hideNotification(), 5000);
    },
    
    hideNotification() {
      this.notification.show = false;
    },
    
    // Hero component styling methods
    getHeroStyle(component) {
      return {
        minHeight: (component.height || 400) + 'px',
        borderRadius: (component.borderRadius || 8) + 'px'
      };
    },
    
    getHeroBackgroundStyle(component) {
      let backgroundStyle = {};
      
      if (component.backgroundImage) {
        if (component.overlay) {
          backgroundStyle.backgroundImage = `linear-gradient(${component.overlayColor || 'rgba(0,0,0,0.3)'}, ${component.overlayColor || 'rgba(0,0,0,0.3)'}), url('${component.backgroundImage}')`;
        } else {
          backgroundStyle.backgroundImage = `url('${component.backgroundImage}')`;
        }
        backgroundStyle.backgroundSize = 'cover';
        backgroundStyle.backgroundPosition = 'center';
        backgroundStyle.backgroundRepeat = 'no-repeat';
      } else if (component.backgroundGradient) {
        backgroundStyle.backgroundImage = component.backgroundGradient;
      } else {
        backgroundStyle.backgroundColor = component.backgroundColor || '#8B5CF6';
      }
      
      return backgroundStyle;
    },
    
    getHeroContentStyle(component) {
      const position = component.contentPosition || 'center-left';
      let alignItems = 'flex-start'; // Horizontal positioning (cross axis)
      let justifyContent = 'center'; // Vertical positioning (main axis)
      let textAlign = component.textAlignment || 'left';
      
      // Split position into parts for better parsing
      const parts = position.split('-');
      const vertical = parts[0]; // top, center, bottom
      const horizontal = parts[1]; // left, center, right
      
      // Handle horizontal positioning (alignItems for flex-direction: column)
      if (horizontal === 'left') {
        alignItems = 'flex-start';
      } else if (horizontal === 'right') {
        alignItems = 'flex-end';
      } else if (horizontal === 'center') {
        alignItems = 'center';
      }
      
      // Handle vertical positioning (justifyContent for flex-direction: column)
      if (vertical === 'top') {
        justifyContent = 'flex-start';
      } else if (vertical === 'bottom') {
        justifyContent = 'flex-end';
      } else if (vertical === 'center') {
        justifyContent = 'center';
      }
      
      return {
        textAlign: textAlign,
        justifyContent: justifyContent,
        alignItems: alignItems,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        padding: '32px'
      };
    },
    
    getHeroTitleStyle(component) {
      return {
        color: component.titleColor || '#ffffff',
        fontSize: (component.titleSize || 36) + 'px',
        fontWeight: 'bold',
        lineHeight: '1.2',
        margin: '0 0 16px 0'
      };
    },
    
    getHeroSubtitleStyle(component) {
      return {
        color: component.subtitleColor || '#ffffff',
        fontSize: (component.subtitleSize || 24) + 'px',
        fontWeight: '500',
        lineHeight: '1.3',
        margin: '0 0 16px 0'
      };
    },
    
    getHeroDescriptionStyle(component) {
      return {
        color: component.descriptionColor || '#ffffff',
        fontSize: (component.descriptionSize || 16) + 'px',
        lineHeight: '1.5',
        margin: '0 0 24px 0'
      };
    },
    
    getHeroButtonStyle(component) {
      return {
        backgroundColor: component.buttonBackgroundColor || '#000000',
        color: component.buttonColor || '#ffffff',
        padding: '12px 24px',
        borderRadius: '6px',
        border: 'none',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-block'
      };
    },

    applyHeroTemplate(templateType) {
      if (!this.selectedComponent || this.selectedComponent.type !== 'hero') return;
      
      const templates = {
        marketing: {
          title: 'Personalised Branded Marketing',
          subtitle: 'Immersive sound. Fine-tuned to you.',
          description: 'Experience the next evolution of marketing comfort.',
          buttonText: 'Learn More',
          buttonUrl: 'https://example.com/marketing',
          backgroundGradient: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 50%, #8B5CF6 100%)',
          backgroundImage: '',
          height: 400,
          borderRadius: 12,
          contentPosition: 'center-left',
          textAlignment: 'left',
          titleColor: '#ffffff',
          titleSize: 36,
          subtitleColor: '#ffffff',
          subtitleSize: 24,
          descriptionColor: '#ffffff',
          buttonBackgroundColor: '#000000',
          buttonColor: '#ffffff'
        },
        announcement: {
          title: 'Important Announcement',
          subtitle: 'Stay informed with our latest updates',
          description: 'We have exciting news to share with you about our upcoming services and offerings.',
          buttonText: 'Read More',
          buttonUrl: 'https://example.com/news',
          backgroundGradient: 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)',
          backgroundImage: '',
          height: 350,
          borderRadius: 8,
          contentPosition: 'center-center',
          textAlignment: 'center',
          titleColor: '#ffffff',
          titleSize: 32,
          subtitleColor: '#ffffff',
          subtitleSize: 20,
          descriptionColor: '#ffffff',
          buttonBackgroundColor: '#ffffff',
          buttonColor: '#3B82F6'
        },
        product: {
          title: 'New Vehicle Arrives',
          subtitle: 'Advanced. Capable. Ready.',
          description: 'Discover the latest generation with enhanced performance and cutting-edge technology.',
          buttonText: 'Explore Now',
          buttonUrl: 'https://example.com/new-vehicles',
          backgroundGradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
          backgroundImage: '',
          height: 450,
          borderRadius: 16,
          contentPosition: 'bottom-left',
          textAlignment: 'left',
          titleColor: '#ffffff',
          titleSize: 40,
          subtitleColor: '#ffffff',
          subtitleSize: 22,
          descriptionColor: '#ffffff',
          buttonBackgroundColor: '#ffffff',
          buttonColor: '#10B981'
        },
        dealership: {
          title: 'Our Dealership',
          subtitle: 'Your trusted automotive partner',
          description: 'Professional service, quality vehicles, and expert advice. Visit us today.',
          buttonText: 'Visit Showroom',
          buttonUrl: 'https://example.com/about-us',
          backgroundGradient: 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)',
          backgroundImage: '',
          height: 380,
          borderRadius: 6,
          contentPosition: 'center-right',
          textAlignment: 'right',
          titleColor: '#ffffff',
          titleSize: 34,
          subtitleColor: '#ffffff',
          subtitleSize: 18,
          descriptionColor: '#ffffff',
          buttonBackgroundColor: '#EF4444',
          buttonColor: '#ffffff'
        }
      };
      
      const template = templates[templateType];
      if (template) {
        Object.assign(this.selectedComponent, template);
        this.updateComponentContent();
      }
    },
    
    moveExistingComponentToColumn(sourceType, sourceData, targetParentIndex, targetColumnIndex) {
      // Safety checks
      if (!this.emailComponents || targetParentIndex >= this.emailComponents.length || targetParentIndex < 0) {
        console.error('Invalid target parent index:', targetParentIndex);
        return;
      }
      
      const targetParent = this.emailComponents[targetParentIndex];
      if (!targetParent || targetParent.type !== 'columns' || !targetParent.columns) {
        console.error('Target parent is not a valid columns component:', targetParent);
        return;
      }
      
      if (targetColumnIndex >= targetParent.columns.length || targetColumnIndex < 0) {
        console.error('Invalid target column index:', targetColumnIndex);
        return;
      }
      
      let componentToMove = null;
      
      // Extract the component from its current location
      if (sourceType === 'main') {
        // Moving from main email body to column
        const sourceIndex = sourceData;
        if (sourceIndex >= this.emailComponents.length || sourceIndex < 0) {
          console.error('Invalid source index:', sourceIndex);
          return;
        }
        
        componentToMove = this.emailComponents[sourceIndex];
        
        // Remove from main area using Vue's reactivity
        this.$delete(this.emailComponents, sourceIndex);
        
        // Adjust selected index if needed
        if (this.selectedComponentIndex === sourceIndex) {
          this.selectedComponentIndex = targetParentIndex;
        } else if (this.selectedComponentIndex > sourceIndex) {
          this.selectedComponentIndex--;
        }
      } else if (sourceType === 'column') {
        // Moving from one column to another column
        const { parentIndex, columnIndex, componentIndex } = sourceData;
        
        // Safety checks for source
        if (!this.emailComponents[parentIndex] || 
            !this.emailComponents[parentIndex].columns ||
            !this.emailComponents[parentIndex].columns[columnIndex] ||
            !this.emailComponents[parentIndex].columns[columnIndex].components ||
            componentIndex >= this.emailComponents[parentIndex].columns[columnIndex].components.length) {
          console.error('Invalid source column component location:', sourceData);
          return;
        }
        
        componentToMove = this.emailComponents[parentIndex].columns[columnIndex].components[componentIndex];
        
        // Remove from source column using Vue's reactivity
        this.$delete(this.emailComponents[parentIndex].columns[columnIndex].components, componentIndex);
      }
      
      if (componentToMove) {
        // Ensure the component has proper ID
        if (!componentToMove.id) {
          componentToMove.id = Date.now();
        }
        
        // Add to target column
        if (!this.emailComponents[targetParentIndex].columns[targetColumnIndex].components) {
          this.$set(this.emailComponents[targetParentIndex].columns[targetColumnIndex], 'components', []);
        }
        
        // Add component to target column using Vue's reactivity
        const targetColumn = this.emailComponents[targetParentIndex].columns[targetColumnIndex];
        targetColumn.components.push(componentToMove);
        
        // Trigger Vue reactivity for the column
        this.$set(this.emailComponents[targetParentIndex].columns, targetColumnIndex, { ...targetColumn });
        
        // Force Vue to re-render
        this.$forceUpdate();
        
        // Update history and show notification
        this.addToHistory();
        this.showNotification({
          type: 'success',
          title: 'Component Moved',
          message: `${this.getBlockTypeName(componentToMove.type)} moved to column successfully`
        });
      } else {
        console.error('No component to move to column found');
      }
    },
    
    moveExistingComponentToMain(sourceType, sourceData) {
      let componentToMove = null;
      
      // Extract the component from its current location
      if (sourceType === 'column') {
        // Moving from column to main email body
        const { parentIndex, columnIndex, componentIndex } = sourceData;
        
        console.log('Moving from column:', parentIndex, columnIndex, componentIndex);
        
        // Safety checks for source column
        if (!this.emailComponents || 
            parentIndex >= this.emailComponents.length || 
            parentIndex < 0 ||
            !this.emailComponents[parentIndex] ||
            !this.emailComponents[parentIndex].columns ||
            columnIndex >= this.emailComponents[parentIndex].columns.length ||
            columnIndex < 0 ||
            !this.emailComponents[parentIndex].columns[columnIndex] ||
            !this.emailComponents[parentIndex].columns[columnIndex].components ||
            componentIndex >= this.emailComponents[parentIndex].columns[columnIndex].components.length ||
            componentIndex < 0) {
          console.error('Invalid source column component location:', sourceData);
          return;
        }
        
        componentToMove = this.emailComponents[parentIndex].columns[columnIndex].components[componentIndex];
        console.log('Source component:', componentToMove);
        
        // Remove from source column using Vue's reactivity
        this.$delete(this.emailComponents[parentIndex].columns[columnIndex].components, componentIndex);
      } else if (sourceType === 'main') {
        // Moving within main area (reordering)
        const sourceIndex = sourceData;
        
        console.log('Moving within main area from index:', sourceIndex);
        
        // Safety checks for source main component
        if (!this.emailComponents || 
            sourceIndex >= this.emailComponents.length || 
            sourceIndex < 0) {
          console.error('Invalid source main component index:', sourceIndex);
          return;
        }
        
        componentToMove = this.emailComponents[sourceIndex];
        
        // Remove from current position using Vue's reactivity
        this.$delete(this.emailComponents, sourceIndex);
      }
      
      if (componentToMove) {
        // Ensure the component has proper structure for main area
        if (!componentToMove.id) {
          componentToMove.id = Date.now();
        }
        
        // Add to end of main email body using Vue's reactivity
        this.$set(this.emailComponents, this.emailComponents.length, componentToMove);
        
        // Select the moved component
        this.selectedComponentIndex = this.emailComponents.length - 1;
        
        // Force Vue to re-render
        this.$forceUpdate();
        
        // Update history and show notification
        this.addToHistory();
        this.showNotification({
          type: 'success',
          title: 'Component Moved',
          message: `${this.getBlockTypeName(componentToMove.type)} moved to main area successfully`
        });
      } else {
        console.error('No component to move found');
      }
    },
  },
  
  mounted() {
    // Load account-specific template if account data is available, otherwise load default
    if (this.accountData && this.accountData[0]) {
      // Account data is available, load account-specific template
      this.$nextTick(() => {
        const accountTemplate = this.templateLibrary.find(t => t.id === 'account-specific');
        if (accountTemplate) {
          this.loadTemplate(accountTemplate);
        } else {
          // Load Volvo style template as fallback
          this.loadTemplate(this.templateLibrary.find(t => t.id === 'volvo-replica'));
        }
      });
    } else {
      // Load Volvo style template by default
      this.loadTemplate(this.templateLibrary.find(t => t.id === 'volvo-replica'));
    }
  }
};
</script>

<style scoped>
.advanced-email-builder {
  height: 100vh;
  max-height: 100vh;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
}

/* Component hover effects */
.group:hover .opacity-0 {
  opacity: 1;
}

/* Smooth transitions */
* {
  transition: all 0.2s ease;
}

/* Custom scrollbars */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Rich text editor focus */
[contenteditable="true"]:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Drag and drop styles */
.email-canvas {
  min-height: 600px;
}

/* Range slider styling */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  outline: none;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
}

input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}

/* Grid classes for columns */
:deep(.grid-cols-1) { 
  grid-template-columns: repeat(1, minmax(0, 1fr)); 
}
:deep(.grid-cols-2) { 
  grid-template-columns: repeat(2, minmax(0, 1fr)); 
}
:deep(.grid-cols-3) { 
  grid-template-columns: repeat(3, minmax(0, 1fr)); 
}

:deep(.gap-1) { gap: 0.25rem; }
:deep(.gap-2) { gap: 0.5rem; }
:deep(.gap-3) { gap: 0.75rem; }
:deep(.gap-4) { gap: 1rem; }
:deep(.gap-5) { gap: 1.25rem; }
:deep(.gap-6) { gap: 1.5rem; }
:deep(.gap-8) { gap: 2rem; }

/* Column drop zone styling */
.column-drop-zone {
  transition: all 0.2s ease;
}

.column-drop-zone:hover,
.column-drop-zone.drag-over {
  background-color: rgba(59, 130, 246, 0.05);
  border-color: #3b82f6;
}

.column-drop-zone.drag-over {
  border-color: #2563eb;
  background-color: #dbeafe;
  border-style: solid;
}

/* Rich Text Editor Styles */
.rich-text-editor {
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
  line-height: 1.5;
}

.rich-text-editor:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}

.rich-text-editor:focus {
  outline: none;
}

/* Ensure proper styling for formatted content */
.rich-text-editor b, .rich-text-editor strong {
  font-weight: bold;
}

.rich-text-editor i, .rich-text-editor em {
  font-style: italic;
}

.rich-text-editor u {
  text-decoration: underline;
}

.rich-text-editor ul, .rich-text-editor ol {
  margin: 8px 0;
  padding-left: 20px;
}

.rich-text-editor li {
  margin: 4px 0;
}

/* Rich Text Toolbar Styles */
.rich-text-toolbar {
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
  border-bottom: 1px solid #e5e7eb;
}

.rich-text-toolbar button:hover {
  background-color: #e5e7eb;
  transform: translateY(-1px);
}

.rich-text-toolbar button:active {
  transform: translateY(0);
  background-color: #d1d5db;
}

.rich-text-toolbar .separator {
  width: 1px;
  height: 24px;
  background-color: #d1d5db;
  margin: 0 8px;
}

/* Canvas Settings Styles */
.canvas-settings input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  outline: none;
}

.canvas-settings input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: #3b82f6;
  cursor: pointer;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.canvas-settings input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #3b82f6;
  cursor: pointer;
  border-radius: 50%;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Drag and drop styles */
.component-palette .component-item {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.component-palette .component-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.column-drop-zone {
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.column-drop-zone:hover,
.column-drop-zone.drag-over {
  background-color: rgba(59, 130, 246, 0.05);
  border-color: #3b82f6;
}

/* Dragging state styles */
.component-dragging {
  opacity: 0.5;
  transform: rotate(2deg);
}

.drop-zone-active {
  background-color: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
  border-style: solid;
}

/* Drag preview styling */
[draggable="true"] {
  cursor: grab;
}

[draggable="true"]:active {
  cursor: grabbing;
}

/* Drag handle specific styling */
.drag-handle {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.drag-handle:hover {
  background-color: #374151;
  transform: scale(1.1);
}

.drag-handle svg {
  pointer-events: none;
}

/* Prevent text selection during drag */
.dragging {
  user-select: none;
  -webkit-user-select: none;
}
</style> 